import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import {
  Avatar,
  Badge,
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Link,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  Tooltip,
  Wrap,
  WrapItem,
  IconButton,
} from '@chakra-ui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Archive,
  ArrowRight,
  ArrowSquareOut,
  Check,
  CheckCircle,
  CircleWavyCheck,
  DotsThreeVertical,
  FileText,
  Icon,
  Info,
  LinkBreak,
  LinkSimple,
  ListChecks,
  LockSimple,
  MapPin,
  Note,
  NotePencil,
  PencilSimple,
  Swap,
  UploadSimple,
  Warning,
} from 'phosphor-react';
import { toast } from 'sonner';
import { useDialog } from '../../../../providers/dialog';
import { useSocietario } from '../../../../hooks/useSocietario';
import type { TipoAlteracao } from '../../../../hooks/useSocietario';
import { IKanbanCard, useKanban } from '../../../../hooks/useKanban';
import { useAuth } from '../../../../providers/auth';
import {
  useLegalizacao,
  ILegalizacao,
  ILegalizacaoUpdate,
  TipoLegalizacao,
  TIPO_LEGALIZACAO_LABEL,
} from '../../../../hooks/useLegalizacao';
import { classificarRotaAlteracao } from '../../contratos/criar/classificarRotaAlteracao';
import {
  COR_TIPO,
  checklistEfetivoDaEtapa,
  etapaAssumida,
  etapaConfigurada,
  etapaPuladaNaRota,
  proximaEtapaAplicavel,
  detalheDoErro,
  etapaDoPlaybook,
  fluxoDaEtapa,
  formatarCnpj,
  formatDuracao,
  obsDoCard,
  PlaybookCampo,
  proximaAcao,
  regimeDoProcesso,
  itemSatisfeito,
  dispensaDoItem,
  dispensasDaEtapa,
  rotuloDoMotivo,
  MOTIVOS_DISPENSA,
  CHAVE_DISPENSAS,
  rotuloComRegistry,
  subpassosAplicaveis,
  tiposAlteracaoDoProcesso,
  TIPOS_ALT_OPCOES,
  viabilidadeRevisar,
  PLAYBOOK,
  CHAVE_EXIGENCIA,
  CHAVE_EXIGENCIA_TEXTO,
  RESPOSTAS_EXIGENCIA,
  RespostaExigencia,
  marcarItem,
  responderExigencia,
  respostasDoOrgao,
  textoDaExigencia,
  CHAVE_ROTA_DEFINIDA,
  CHAVE_SIMPLES,
  CHAVE_SIMPLES_MOTIVO,
  RESPOSTAS_SIMPLES,
  motivoDoSimples,
  CHAVE_CLIENTE,
  naoSeraCliente,
  camposEfetivosDaEtapa,
  linkDaEtapa,
  protocoloDoProcesso,
} from '../legalizacaoPlaybook';
import { resolverRegistry } from '../registries';
import { DispensarItemModal } from './DispensarItemModal';
import { ArquivarProcessoModal } from './ArquivarProcessoModal';
import { FinalizarProcessoModal } from './FinalizarProcessoModal';
import { RenomearProcessoModal } from './RenomearProcessoModal';
import { LocalDoProcesso, TrocarLocalModal } from './TrocarLocalModal';
import { CardAnexos } from './CardAnexos';
import { KanbanCardModal } from '../../../../components/kanban/KanbanCardModal';
import { EtapaReadOnly } from './EtapaReadOnly';
import { FluxoIcons } from './FluxoIcons';
import { ResponsavelPicker } from './ResponsavelPicker';
import { SlaPrazo } from './SlaPrazo';
import { VincularContratoModal } from './VincularContratoModal';
import {
  estadoDoContrato,
  rotuloDoStatus,
  urlDaListagem,
  urlParaContinuar,
} from '../contratoDoCard';

/** Itens de "documento gerado" que o arquivo feito fora satisfaz, e onde fica o nome dele (ver `docDeForaMutation`). */
const ITENS_DO_DOCUMENTO = ['contrato_gerado', 'doc_gerado'];
const CHAVE_DOC_DE_FORA = 'documento_de_fora';

// Estilo de cada bolinha do stepper por estado (evita ternário aninhado).
const STEP_STYLE = {
  done: { bg: 'green.500', color: 'white', border: 'green.500', label: 'green.600', weight: '400' },
  now: { bg: 'white', color: 'blue.500', border: 'blue.400', label: 'blue.500', weight: '700' },
  todo: { bg: 'gray.200', color: 'gray.400', border: 'gray.200', label: 'gray.400', weight: '400' },
} as const;

interface LegalizacaoDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  legalizacao: ILegalizacao | null;
  card: IKanbanCard | null;
  columnTitle: string | null;
  colunas: string[];
  tipo: TipoLegalizacao;
}

/**
 * Titulo de BLOCO — 2026-08-11.
 *
 * Era 10px / `gray.400` / versalete espacejado: vestido de sobrelinha decorativa fazendo
 * trabalho estrutural. Dois problemas de uma vez.
 *
 * Contraste: `gray.400` (#A0AEC0) sobre branco da **2,35:1**, quando o minimo WCAG AA
 * pra texto normal e 4,5:1. A 10px nao ha isencao de "texto grande" — era ilegivel por
 * medida, nao por gosto. `gray.700` da 11,6:1.
 *
 * Hierarquia: como e' o UNICO rotulo de cada bloco, tem que ler como TITULO. Caixa alta
 * espacejada a 10px e' tratamento de metadado secundario.
 */

/**
 * "?" de ajuda — o texto longo sai da tela e fica a um hover/foco de distância.
 *
 * A operação leu o drawer como "poluído", e a maior parte do volume eram três blocos de
 * texto PERMANENTE antes do trabalho: a nota da etapa, a nota da ação e o aviso do
 * Facilita SP. São explicações que se lê UMA vez e depois só ocupam espaço — e a da ação
 * ainda falava em jargão nosso ("gerador v3", "AUDITOR de fidelidade", "vigente ×
 * render"), vocabulário de quem construiu, não de quem opera.
 *
 * É `button` e não `span` de propósito: hover sozinho não alcança quem navega por
 * teclado nem quem usa toque. Com foco visível, o texto continua acessível a todos.
 */
const Ajuda: React.FC<{ texto: React.ReactNode; rotulo?: string }> = ({
  texto,
  rotulo = 'Ver explicação',
}) => (
  <Tooltip hasArrow label={texto} placement="top" maxW="380px" openDelay={150} closeDelay={120}>
    <Box
      as="button"
      type="button"
      aria-label={rotulo}
      onClick={e => e.preventDefault()}
      w="17px"
      h="17px"
      lineHeight="15px"
      borderRadius="full"
      borderWidth="1px"
      borderColor="gray.300"
      color="gray.500"
      bg="white"
      fontSize="11px"
      fontWeight="700"
      flexShrink={0}
      cursor="help"
      _hover={{ borderColor: 'purple.300', color: 'purple.500' }}
      _focusVisible={{ outline: '2px solid', outlineColor: 'purple.400', outlineOffset: '1px' }}
    >
      ?
    </Box>
  </Tooltip>
);

const SectionTitle: React.FC<{
  children: React.ReactNode;
  icon?: Icon;
  accent?: string;
  /** Texto longo da seção — vira "?" ao lado do título em vez de bloco na tela. */
  ajuda?: React.ReactNode;
}> = ({ children, icon: IconComp, accent = '#7C3AED', ajuda }) => (
  <Flex align="center" gap={2} mb={3}>
    {IconComp && (
      <Flex
        w="22px"
        h="22px"
        borderRadius="md"
        align="center"
        justify="center"
        bg={`${accent}1F`}
        color={accent}
        flexShrink={0}
      >
        <IconComp size={13} weight="duotone" />
      </Flex>
    )}
    {/* `gray.600` e nao `gray.700` (2026-08-13): com os tres blocos da faixa de tempo
        igualados em TAMANHO e PESO, a cor virou o unico canal livre pra separar o titulo
        do valor que vem embaixo. `gray.700` contra o `gray.900` do valor era diferenca
        que ninguem enxerga. Aqui ainda da 7,5:1 sobre branco, bem acima do minimo AA —
        o problema historico deste rotulo era `gray.400` a 10px (2,35:1), nao cinza medio. */}
    <Text fontSize="13px" color="gray.600" fontWeight="700" letterSpacing="-0.005em">
      {children}
    </Text>
    {ajuda && <Ajuda texto={ajuda} />}
  </Flex>
);

/**
 * Nomes da tela para a aba Atividade. Quem decide o que mudou é o back (antes x depois do
 * salvamento); daqui vão só os rótulos, que moram no playbook. Sem eles, a linha mostra a
 * chave crua ("dbe_transmitido").
 */
const rotulosParaAtividade = (
  tipo: TipoLegalizacao,
  coluna: string | null | undefined,
  leg: ILegalizacao,
): Record<string, string> => {
  const rotulos: Record<string, string> = {};
  // Os itens que o regime tira (MEI) também levam o nome: trocar o regime e marcar antes de salvar mostrava a chave crua.
  (etapaDoPlaybook(tipo, coluna)?.checklist ?? []).forEach(c => {
    rotulos[c.key] = c.label;
  });
  checklistEfetivoDaEtapa(
    tipo,
    coluna,
    leg.junta_uf,
    leg.municipio,
    tiposAlteracaoDoProcesso(leg.estado_etapas),
    regimeDoProcesso(leg.estado_etapas),
  ).forEach(c => {
    rotulos[c.key] = c.label;
  });
  (etapaDoPlaybook(tipo, coluna)?.campos ?? []).forEach(c => {
    rotulos[c.key] = rotuloComRegistry(c, leg.junta_uf, leg.municipio);
  });
  rotulos[CHAVE_EXIGENCIA_TEXTO] = 'Exigência';
  rotulos[CHAVE_SIMPLES_MOTIVO] = 'Motivo do Simples indeferido';
  rotulos[CHAVE_DOC_DE_FORA] = 'Arquivo feito fora do sistema';
  MOTIVOS_DISPENSA.forEach(m => {
    rotulos[`motivo:${m.key}`] = m.label;
  });
  TIPOS_ALT_OPCOES.forEach(t => {
    rotulos[`tipo:${t.value}`] = t.label;
  });
  return rotulos;
};

/**
 * Rótulo de seção da barra lateral — o mesmo desenho do modal genérico (Prioridade, Expiração),
 * um tom mais escuro: o `gray.400` de lá, a 11px sobre o cinza da lateral, fica em 2,3:1.
 */
const RotuloLateral: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Text
    fontSize="11px"
    fontWeight="600"
    color="gray.500"
    textTransform="uppercase"
    letterSpacing="0.05em"
    mb={1.5}
  >
    {children}
  </Text>
);

/**
 * Mensagem que o backend mandou, quando mandou.
 *
 * O FastAPI devolve a explicação em `response.data.detail`. Sem isto, uma recusa por
 * regra de negócio (422) chega ao operador como "tente novamente" — ele tenta de novo,
 * falha de novo, e a regra nunca é comunicada.
 */
// Estado editável da etapa: campos 'estado' + checklist ficam no mesmo dict (keys distintas).
type EstadoEtapa = Record<string, unknown>;

export const LegalizacaoDrawer: React.FC<LegalizacaoDrawerProps> = ({
  isOpen,
  onClose,
  legalizacao,
  card,
  columnTitle,
  colunas,
  tipo,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { arquivarCard, updateCard, uploadAnexo } = useKanban();
  const { atualizarLegalizacao, moverLegalizacao, finalizarLegalizacao } = useLegalizacao();
  const { getContrato } = useSocietario();

  // subtypeForm = campos tipados do subtype DESTA etapa; estadoEtapa = campos 'estado' + checklist.
  const [subtypeForm, setSubtypeForm] = useState<Record<string, unknown>>({});
  const [estadoEtapa, setEstadoEtapa] = useState<EstadoEtapa>({});
  const [tiposAlt, setTiposAlt] = useState<TipoAlteracao[]>([]);
  const [respNome, setRespNome] = useState<string | null>(null);
  const [salvoOk, setSalvoOk] = useState(false);
  const [viewingCol, setViewingCol] = useState<string | null>(null);
  // Quem dispensou vai gravado junto do motivo — mesmo par (quem/quando) que o
  // histórico de prazo já registra em `_sla`. Justificativa sem autor não é trilha.
  const { user: usuarioLogado } = useAuth();
  const usuarioNome = usuarioLogado?.nome ?? null;
  /** Item aguardando confirmação no modal de dispensa (`null` = modal fechado). */
  const [dispensando, setDispensando] = useState<{ key: string; label: string } | null>(null);

  // Contrato linkado (etapa "Alteração + Consolidação"): usado pra DERIVAR o gate do
  // estado REAL do contrato (docx gerado + vistoria de fidelidade aprovada), em vez de
  // depender de checkbox manual. Reabrir o card após aprovar a vistoria satisfaz o gate.
  const contratoVinculadoId = legalizacao?.contrato_societario_id ?? null;
  const {
    data: contratoVinculadoResp,
    isSuccess: contratoCarregou,
    error: erroDoContrato,
    refetch: releContrato,
  } = useQuery({
    queryKey: ['societario-contrato', contratoVinculadoId],
    queryFn: getContrato,
    enabled: !!contratoVinculadoId,
    // 404 = o vinculado não existe mais (rascunho descartado): sem nova tentativa.
    retry: (tentativas, erro: unknown) =>
      (erro as { response?: { status?: number } })?.response?.status !== 404 && tentativas < 1,
  });
  const contratoSumiu =
    (erroDoContrato as { response?: { status?: number } } | null)?.response?.status === 404;
  // Outra falha (500, rede): não é "sumiu", mas também não pode girar para sempre.
  const falhouLeituraDoContrato = !!erroDoContrato && !contratoSumiu;
  const contratoVinculado = contratoVinculadoResp?.data ?? null;

  // Itens DERIVADOS do contrato linkado (etapa com ⭐ ação: "Contrato" na constituição,
  // "Alteração + Consolidação" na alteração): não são checkbox manual — refletem o estado
  // real. `doc_gerado`/`contrato_gerado` = docx existe (contrato OU, no futuro, RE do EI);
  // `auditoria_ok` = vistoria de fidelidade aprovada. Assim, gerar o contrato (ou aprovar a
  // vistoria na lupinha) satisfaz o gate automaticamente ao reabrir o card, sem marcar à mão.
  const doneDerivadoContrato = (key: string): boolean => {
    if (key === 'doc_gerado' || key === 'contrato_gerado')
      return !!contratoVinculado?.contrato_s3_key;
    if (key === 'auditoria_ok') return !!contratoVinculado?.auditoria_revisada_em;
    return false;
  };
  /** Chaves que o contrato vinculado governa — quando ele existe (senão são manuais). */
  const CHAVES_DERIVADAS = ['contrato_gerado', 'doc_gerado', 'auditoria_ok'];
  // O contrato só é FONTE DA VERDADE destas chaves quando existe um contrato vinculado E a
  // query dele já resolveu. As duas condições importam, por motivos diferentes:
  //
  //  - query em voo: `contratoVinculado` é null enquanto carrega, então derivar aqui gravaria
  //    `false` por cima de um item legitimamente concluído;
  //  - sem contrato vinculado: o item é MANUAL e o operador é quem marca — `renderCheckRow`
  //    já entrega um checkbox editável nesse caso. É o fluxo do Empresário Individual, cujo
  //    Requerimento é feito fora do sistema (nota da própria etapa "Contrato" no playbook).
  //    Tratar "sem contrato" como "derivado = false" apagaria a marcação do operador.
  const contratoEhFonteDaVerdade = !!contratoVinculadoId && contratoCarregou;

  // ── Aceite do cliente: marca MANUAL, porém VERSIONADA ───────────────────────────
  // "Cliente aprovou" não é sobre o processo, é sobre UM documento — o que ele leu. Se
  // o contrato for regerado com conteúdo diferente, a aprovação vira mentira: o cliente
  // aprovou uma versão que não existe mais. É a mesma família do carimbo de auditoria.
  //
  // O sinal é o `contrato_hash` = sha256 dos BYTES do docx, gravado pelo backend em todo
  // caminho de geração. Melhor que timestamp: regerar e sair byte-idêntico NÃO invalida —
  // o cliente aprovou exatamente aquele documento.
  const CHAVE_APROVACAO = 'cliente_aprovou';
  const CHAVE_APROVACAO_HASH = 'cliente_aprovou_hash';
  // Versões DISTINTAS do documento que este card já viu (hashes, em ordem). O tamanho
  // menos um é o número de regerações — quantas rodadas de "cliente não aprovou, refaz".
  //
  // ⚠️ Conta o que o CARD observou, não o que o banco registrou. A contagem autoritativa
  // é `tb_societario_v2_geracao_log` (1 linha por geração), mas nenhum endpoint a expõe
  // hoje. Consequência: regerar DUAS vezes seguidas pela listagem de contratos, sem
  // reabrir o card no meio, conta como uma. Subestima, nunca superestima.
  const CHAVE_VERSOES = 'doc_versoes';
  const hashAtual = contratoVinculado?.contrato_hash ?? null;
  const versoesVistas = Array.isArray(estadoEtapa[CHAVE_VERSOES])
    ? (estadoEtapa[CHAVE_VERSOES] as string[])
    : [];
  const regeracoes = Math.max(0, versoesVistas.length - 1);
  /** A aprovação registrada vale para o documento que está no card AGORA? */
  const aprovacaoVigente = (estado: EstadoEtapa): boolean => {
    if (!estado[CHAVE_APROVACAO]) return false;
    // Sem contrato vinculado (fluxo do Empresário Individual, documento feito fora do
    // sistema) não há hash a comparar — a marca do operador é soberana.
    if (!contratoEhFonteDaVerdade) return true;
    return !!hashAtual && estado[CHAVE_APROVACAO_HASH] === hashAtual;
  };

  const etapa = etapaDoPlaybook(tipo, columnTitle);
  const idx = columnTitle ? colunas.indexOf(columnTitle) : -1;
  // Rota efetiva: a seleção atual (etapa Definição+Rota, antes de salvar) OU a persistida.
  const rotaResultado = classificarRotaAlteracao(tiposAlt);
  // "Rota definida" sai dos tipos marcados na etapa (19/10, Camila: "com base na escolha, o ideal não seria
  // marcar sozinho?"): ninguém marca à mão, e o salvar grava o que os tipos dizem.
  const etapaTemTipos = (etapa?.campos ?? []).some(c => c.origem === 'tipos');
  const rotaPelosTipos = (key: string) => etapaTemTipos && key === CHAVE_ROTA_DEFINIDA;
  const rotaEfetiva = rotaResultado.rota ?? (legalizacao?.rota as 'A' | 'B' | null) ?? null;
  // "Não" ao "Será cliente?" pula "Demais órgãos" e o Licenciamento da constituição; vale a resposta da tela, antes de salvar.
  const naoCliente = naoSeraCliente(
    columnTitle
      ? { ...legalizacao?.estado_etapas, [columnTitle]: estadoEtapa }
      : legalizacao?.estado_etapas,
  );
  // Próxima etapa. A Rota B pula as etapas `somenteRotaA` (viabilidade e licenciamento).
  const proxima = proximaEtapaAplicavel(tipo, colunas, idx, rotaEfetiva, naoCliente);
  const coletadas = colunas
    .slice(0, Math.max(idx, 0))
    .filter(c => !etapaPuladaNaRota(tipo, c, rotaEfetiva, naoCliente));

  // (Re)inicializa o form ao abrir, ao trocar de processo ou etapa e quando o que o form
  // mostra mudou no servidor. Reler o mesmo processo (volta da aba do contrato, comentário,
  // renomear) não apaga o que está só na tela: a assinatura dos campos não muda.
  const assinaturaDoFormRef = useRef<string | null>(null);
  useEffect(() => {
    if (!legalizacao || !isOpen) {
      assinaturaDoFormRef.current = null;
      return;
    }
    const et = etapaDoPlaybook(tipo, columnTitle);
    const sf: Record<string, unknown> = {};
    (et?.campos ?? []).forEach(c => {
      if (c.origem === 'subtype')
        sf[c.key] = (legalizacao as unknown as Record<string, unknown>)[c.key] ?? '';
    });
    const est = ((legalizacao.estado_etapas as Record<string, EstadoEtapa> | null)?.[
      columnTitle ?? ''
    ] ?? {}) as EstadoEtapa;
    const assinatura = JSON.stringify([legalizacao.id, tipo, columnTitle, sf, est]);
    if (assinatura === assinaturaDoFormRef.current) return;
    assinaturaDoFormRef.current = assinatura;
    setSubtypeForm(sf);
    setEstadoEtapa(est);
    setTiposAlt((est.tipos_alteracao as TipoAlteracao[]) ?? []);
    setViewingCol(null);
  }, [legalizacao, columnTitle, isOpen, tipo]);

  // O responsável é do processo, não do form da etapa: acompanha o servidor sem reiniciá-lo.
  const responsavelNoServidor = legalizacao?.responsavel_nome ?? null;
  useEffect(() => {
    setRespNome(responsavelNoServidor);
  }, [responsavelNoServidor]);

  /**
   * O nome já foi aprovado na viabilidade?
   *
   * ⚠️ Lido da etapa de VIABILIDADE, que quase nunca é a coluna aberta — a pergunta só
   * importa quando o processo já passou por ela. Por isso não reusa `estadoEtapa`, que é
   * o estado da coluna ATUAL.
   *
   * A chave varia: `nome_aprovado` no registry e na constituição estática,
   * `nome_end_aprovado` no fallback de alteração. Em vez de listar as variações (e
   * esquecer a próxima), procura no CHECKLIST EFETIVO da UF o item cuja chave fala de
   * nome, e pergunta se ele está marcado. Assim acompanha o registry sem duplicar regra.
   */
  const nomeAprovadoNaViabilidade = useMemo(() => {
    if (!legalizacao) return false;
    const colViab = Object.keys(PLAYBOOK[tipo] ?? {}).find(c =>
      c.toLowerCase().startsWith('viabilidade'),
    );
    if (!colViab) return false; // baixa não tem viabilidade
    const est = ((legalizacao.estado_etapas as Record<string, EstadoEtapa> | null)?.[colViab] ??
      {}) as EstadoEtapa;
    return checklistEfetivoDaEtapa(
      tipo,
      colViab,
      legalizacao.junta_uf,
      legalizacao.municipio,
      tiposAlteracaoDoProcesso(legalizacao.estado_etapas),
      regimeDoProcesso(legalizacao.estado_etapas),
    ).some(i => /nome/i.test(i.key) && !!est[i.key]);
  }, [legalizacao, tipo]);

  const invalidar = () => {
    queryClient.invalidateQueries({ queryKey: ['legalizacoes'] }); // prefixo: per-tipo + dashboard
    queryClient.invalidateQueries({ queryKey: ['kanban-board'] });
    // A aba Atividade ganha a linha do que acabou de ser salvo.
    queryClient.invalidateQueries({ queryKey: ['kanban-atividades'] });
  };

  // Foi ao gerador (ou à listagem) de contratos a partir deste card: ao VOLTAR para esta aba,
  // recarrega. O gerador vincula o contrato ao processo na aba dele, e o admin não recarrega
  // ao ganhar foco — o botão seguia "Gerar…" e um 2º clique abria outro gerador. Reler não
  // apaga o que está só na tela (form por assinatura; comentário só some ao trocar de card).
  const foiAoContratoRef = useRef(false);
  const releuEmRef = useRef(0);
  useEffect(() => {
    if (!isOpen) {
      foiAoContratoRef.current = false;
      return undefined;
    }
    const aoVoltar = () => {
      if (!foiAoContratoRef.current || document.visibilityState === 'hidden') return;
      // `focus` e `visibilitychange` chegam juntos na volta: uma leitura só.
      if (Date.now() - releuEmRef.current < 2000) return;
      releuEmRef.current = Date.now();
      queryClient.invalidateQueries({ queryKey: ['legalizacoes'] });
      queryClient.invalidateQueries({ queryKey: ['kanban-board'] });
      queryClient.invalidateQueries({ queryKey: ['societario-contrato'] });
    };
    window.addEventListener('focus', aoVoltar);
    document.addEventListener('visibilitychange', aoVoltar);
    return () => {
      window.removeEventListener('focus', aoVoltar);
      document.removeEventListener('visibilitychange', aoVoltar);
    };
  }, [isOpen, queryClient]);

  // Arquivar tira o CARD do quadro (`arquivado_em`); a legalizacao continua
  // intacta no banco e volta pelo bloco "Arquivados". Mesmo contrato do
  // estatuto (EstatutoDrawer.handleArquivar) — endpoint e semantica ja existiam,
  // faltava so o caminho na UI da legalizacao.
  const [arquivarAberto, setArquivarAberto] = useState(false);
  const [renomearAberto, setRenomearAberto] = useState(false);
  const [finalizarAberto, setFinalizarAberto] = useState(false);

  /**
   * Renomear = trocar o TÍTULO DO CARD. O nome da empresa não tem coluna própria na
   * legalização: o backend grava `titulo=obj_in.razao_social` na criação, e
   * `ILegalizacaoUpdate` não inclui `razao_social`. Então o caminho é o mesmo que o
   * `SlaPrazo` usa pro prazo — `updateCard`, sem tocar no backend.
   */
  const renomearMutation = useMutation({
    mutationFn: ({ cardId, titulo }: { cardId: number; titulo: string }) =>
      updateCard({ id: cardId, titulo }),
    onSuccess: () => {
      toast.success('Nome atualizado');
      // `invalidar()` cobre board e legalizações; o título aparece no card do quadro, na
      // busca e no cabeçalho deste drawer — os três leem do mesmo cache.
      invalidar();
      setRenomearAberto(false);
    },
    onError: (erro: unknown) =>
      toast.error(detalheDoErro(erro, 'Não foi possível alterar o nome. Tente novamente.')),
  });

  // Trocar estado/município depois de criado (19/10, Camila: empresa que muda de estado). O back aceita os três
  // campos no PATCH e a aba Atividade registra a troca.
  const [trocarLocalAberto, setTrocarLocalAberto] = useState(false);
  const trocarLocalMutation = useMutation({
    mutationFn: (local: LocalDoProcesso) => atualizarLegalizacao(legalizacao!.id, local),
    onSuccess: () => {
      toast.success('Local atualizado');
      invalidar();
      setTrocarLocalAberto(false);
    },
    onError: (erro: unknown) =>
      toast.error(detalheDoErro(erro, 'Não foi possível trocar o local. Tente novamente.')),
  });

  // Contrato (ou documento) feito FORA do sistema (19/10, Camila: "vínculo de contratos criados à parte"): o arquivo
  // vai para os anexos do card e o item de documento gerado da etapa fica marcado. Grava ao salvar a etapa, como
  // qualquer marcação; o nome do arquivo fica no estado para a caixa da ação mostrar.
  const docDeForaRef = useRef<HTMLInputElement>(null);
  // O drawer é um só para todos os cards e o `onSuccess` roda com o render mais novo: o card e a etapa do envio vão
  // nas variáveis, e a marcação só entra se a tela ainda for a deles (upload lento + troca de card no meio).
  const docDeForaMutation = useMutation({
    mutationFn: (v: { arquivo: File; cardId: number; legId: number; coluna: string | null }) =>
      uploadAnexo(v.cardId, v.arquivo),
    onSuccess: (_resposta, v) => {
      queryClient.invalidateQueries({ queryKey: ['kanban-anexos', v.cardId] });
      queryClient.invalidateQueries({ queryKey: ['kanban-atividades', v.cardId] });
      if (legalizacao?.id !== v.legId || columnTitle !== v.coluna) {
        toast.success('Arquivo anexado. Marque o documento como gerado no card dele.');
        return;
      }
      const item = (etapa?.checklist ?? []).find(c => ITENS_DO_DOCUMENTO.includes(c.key));
      if (item) setEstado(item.key, true);
      setEstado(CHAVE_DOC_DE_FORA, v.arquivo.name);
      toast.success('Arquivo anexado e documento marcado como gerado. Salve a etapa para gravar.');
    },
    onError: () => toast.error('Não foi possível anexar o arquivo.'),
  });
  const escolheuDocDeFora = (e: React.ChangeEvent<HTMLInputElement>) => {
    const arquivo = e.target.files?.[0];
    e.target.value = ''; // permite reescolher o mesmo arquivo
    if (!arquivo || !card?.id || !legalizacao) return;
    const prefixo = tipo === 'constituicao' ? 'CONTRATO-FEITO-FORA' : 'DOCUMENTO-FEITO-FORA';
    docDeForaMutation.mutate({
      arquivo: new File([arquivo], `${prefixo}_${arquivo.name}`, { type: arquivo.type }),
      cardId: card.id,
      legId: legalizacao.id,
      coluna: columnTitle ?? null,
    });
  };

  const arquivarMutation = useMutation({
    mutationFn: ({ cardId, motivo }: { cardId: number; motivo: string }) =>
      arquivarCard(cardId, motivo || undefined),
    onSuccess: () => {
      toast.success('Processo arquivado');
      invalidar();
      queryClient.invalidateQueries({ queryKey: ['legalizacao-arquivados'] });
      setArquivarAberto(false);
      onClose();
    },
    onError: () => toast.error('Não foi possível arquivar o processo.'),
  });

  const reatribuir = (userId: number, nome: string) => {
    setRespNome(nome); // otimista
    atualizarLegalizacao(legalizacao!.id, { responsavel_id: userId })
      .then(() => {
        toast.success(`Responsável: ${nome}`);
        invalidar();
      })
      .catch(() => {
        setRespNome(responsavelNoServidor); // desfaz o otimista
        toast.error('Não foi possível reatribuir.');
      });
  };

  // `contratoMudou`: o salvamento que acompanha vincular, trocar ou desvincular o contrato.
  const montarPatch = (opcoes: { contratoMudou?: boolean } = {}): ILegalizacaoUpdate => {
    const patch: Record<string, unknown> = { ...subtypeForm };
    // Campos tipados carregados do form controlado ficam '' quando intocados; o backend
    // rejeita '' em coluna numérica (ex.: dare_valor → 400, trava o avanço da Coleta RFB).
    // Coage '' → null antes de enviar.
    Object.keys(patch).forEach(k => {
      if (patch[k] === '') patch[k] = null;
    });
    // tipos_alteracao (Definição+Rota) → grava a rota derivada + persiste os tipos no estado.
    const estadoFinal: EstadoEtapa = { ...estadoEtapa };
    // O que o SISTEMA escreve neste salvamento (não é ação de quem salvou): a aba Atividade não
    // registra a marcação dessas chaves. Dispensar continua sendo de quem salvou; a dispensa
    // que o próprio sistema tira vai como `dispensa:<chave>`.
    const automaticos: string[] = [];
    // Persiste os itens DERIVADOS do contrato em QUALQUER etapa com ⭐ ação ("Contrato" na
    // constituição, "Alteração + Consolidação" na alteração). Assim o board e o drag-gate (que
    // leem estado_etapas cru, sem o derivado) concordam com o gate do drawer, sem buscar o
    // contrato por card. Carimba só as chaves que a PRÓPRIA etapa declara — "Contrato" tem
    // `contrato_gerado`, "Alteração + Consolidação" tem `doc_gerado` + `auditoria_ok`.
    //
    // 2026-08-11: o carimbo é BIDIRECIONAL. Antes só escrevia `true`, nunca `false`, e como
    // `doneEfetivo` é um OR com o derivado, o selo sobrevivia à condição deixar de valer. Toda
    // (re)geração v3-alteração ZERA a vistoria no backend (societario.py, "toda (re)geracao
    // v3-alteracao RESETA a revisao"), então um card já salvo com a auditoria aprovada ficava
    // verde e com o "Avançar" liberado depois de regerar — enquanto o download do docx era
    // barrado com 409 AUDITORIA_NAO_REVISADA. O card mentia sobre o documento.
    if (etapa?.acao?.tipoOperacao && contratoEhFonteDaVerdade) {
      (etapa.checklist ?? []).forEach(c => {
        if (CHAVES_DERIVADAS.includes(c.key)) {
          estadoFinal[c.key] = doneDerivadoContrato(c.key);
          automaticos.push(c.key);
        }
      });
      /**
       * Dispensa que o próprio sistema tornou desnecessária SAI.
       *
       * Fluxo real: o operador dispensa "Documento gerado" porque o gerador não cobria o
       * ato, e mais tarde o caso passa a ser coberto — ou ele simplesmente gera. A
       * dispensa vira mentira: o card diria "não se aplica" com o docx no S3 do lado.
       *
       * ⚠️ SÓ quando o contrato responde SIM. Se ele responde NÃO — documento ausente,
       * auditoria reprovada — a dispensa PERMANECE. É exatamente aí que ela existe pra
       * valer, e apagá-la seria o sistema desfazendo, sem avisar, uma decisão que alguém
       * tomou, assinou e justificou.
       */
      const semDispensaObsoleta = { ...dispensasDaEtapa(estadoFinal) };
      let mexeu = false;
      (etapa.checklist ?? []).forEach(c => {
        if (
          CHAVES_DERIVADAS.includes(c.key) &&
          semDispensaObsoleta[c.key] &&
          doneDerivadoContrato(c.key)
        ) {
          delete semDispensaObsoleta[c.key];
          automaticos.push(`dispensa:${c.key}`);
          mexeu = true;
        }
      });
      if (mexeu) estadoFinal[CHAVE_DISPENSAS] = semDispensaObsoleta;
    }
    // Registra a versão do documento que este card está vendo agora. Append-only e só
    // quando o hash MUDA — regerar e sair byte-idêntico não conta como nova versão.
    if (
      contratoEhFonteDaVerdade &&
      hashAtual &&
      versoesVistas[versoesVistas.length - 1] !== hashAtual
    ) {
      estadoFinal[CHAVE_VERSOES] = [...versoesVistas, hashAtual];
    }
    // Aceite do cliente: ao MARCAR, carimba o hash do documento aprovado; ao desmarcar,
    // limpa. E se a aprovação salva não vale mais para o docx atual (regeração mudou o
    // conteúdo), grava `false` — senão o board e o drag-gate, que leem o estado cru,
    // continuariam vendo verde.
    // O hash da aprovação é carimbado no CLIQUE (`setAprovacaoCliente`). Aqui só se
    // REBAIXA a aprovação que envelheceu — o caso em que o documento foi regerado
    // enquanto o card estava fechado. Sem isto, o board e o drag-gate, que leem o estado
    // cru, continuariam vendo verde.
    if ((etapa?.checklist ?? []).some(c => c.key === CHAVE_APROVACAO)) {
      if (!estadoFinal[CHAVE_APROVACAO]) {
        delete estadoFinal[CHAVE_APROVACAO_HASH];
      } else if (contratoEhFonteDaVerdade && estadoFinal[CHAVE_APROVACAO_HASH] !== hashAtual) {
        estadoFinal[CHAVE_APROVACAO] = false;
        delete estadoFinal[CHAVE_APROVACAO_HASH];
        automaticos.push(CHAVE_APROVACAO);
      }
    }
    // O que vinha do contrato anterior deixa de valer ao trocar ou desvincular: os itens
    // derivados dele e a aprovação do cliente presa ao documento dele (a que tem hash). O
    // contrato novo volta a carimbar no próximo salvamento; sem contrato, ficam à mão.
    if (opcoes.contratoMudou && etapa?.acao?.tipoOperacao) {
      (etapa.checklist ?? []).forEach(c => {
        if (CHAVES_DERIVADAS.includes(c.key)) {
          estadoFinal[c.key] = false;
          if (!automaticos.includes(c.key)) automaticos.push(c.key);
        }
      });
      if (estadoFinal[CHAVE_APROVACAO_HASH]) {
        estadoFinal[CHAVE_APROVACAO] = false;
        delete estadoFinal[CHAVE_APROVACAO_HASH];
        if (!automaticos.includes(CHAVE_APROVACAO)) automaticos.push(CHAVE_APROVACAO);
      }
      // As versões contadas eram do documento anterior: outro contrato não é "regerado".
      delete estadoFinal[CHAVE_VERSOES];
    }
    if (etapaTemTipos) {
      patch.rota = rotaResultado.rota;
      estadoFinal.tipos_alteracao = tiposAlt;
      if ((etapa?.checklist ?? []).some(c => c.key === CHAVE_ROTA_DEFINIDA)) {
        estadoFinal[CHAVE_ROTA_DEFINIDA] = !!rotaResultado.rota;
        automaticos.push(CHAVE_ROTA_DEFINIDA);
      }
    }
    // O spread mantém as chaves de topo (a observação antiga `_obs`, o `_sla`...).
    patch.estado_etapas = {
      ...((legalizacao?.estado_etapas as Record<string, unknown>) ?? {}),
      [columnTitle ?? '']: estadoFinal,
    };
    if (legalizacao)
      patch.atividade = {
        rotulos: rotulosParaAtividade(tipo, columnTitle, legalizacao),
        automaticos,
      };
    return patch as ILegalizacaoUpdate;
  };

  // Vincular, trocar ou desvincular o contrato (null), junto com o que está só na tela, num
  // PATCH só: se o back recusar o contrato (422), nada muda — nem a etapa.
  const [vincularAberto, setVincularAberto] = useState(false);
  const showDialog = useDialog();
  const vincularMutation = useMutation({
    mutationFn: async (contratoId: number | null) =>
      atualizarLegalizacao(legalizacao!.id, {
        ...montarPatch({ contratoMudou: true }),
        contrato_societario_id: contratoId,
      }),
    onSuccess: (_, contratoId) => {
      toast.success(contratoId ? `Contrato #${contratoId} vinculado` : 'Contrato desvinculado');
      invalidar();
      queryClient.invalidateQueries({ queryKey: ['societario-contrato'] });
      setVincularAberto(false);
    },
    onError: (erro: unknown) =>
      toast.error(detalheDoErro(erro, 'Não foi possível alterar o contrato do card.')),
  });

  // Salvar, Avançar e Finalizar registram antes o comentário digitado e não enviado (o
  // modal genérico entrega a função aqui): sem isso, quem digita e avança perde o texto.
  const registrarComentarioRef = useRef<(() => Promise<void>) | null>(null);
  const guardarEnvioDoComentario = useCallback((enviar: (() => Promise<void>) | null) => {
    registrarComentarioRef.current = enviar;
  }, []);
  const registrarComentarioPendente = () => registrarComentarioRef.current?.();

  // `desvincular`: o vinculado sumiu e o gerador vai ligar outro (ele só liga processo livre).
  const salvarMutation = useMutation({
    mutationFn: async (opcoes?: { contratoMudou?: boolean; desvincular?: boolean }) => {
      await registrarComentarioPendente();
      const patch = montarPatch(opcoes);
      if (opcoes?.desvincular) patch.contrato_societario_id = null;
      return atualizarLegalizacao(legalizacao!.id, patch);
    },
    onSuccess: () => {
      toast.success('Etapa salva');
      invalidar();
      setSalvoOk(true);
      setTimeout(() => setSalvoOk(false), 1500);
    },
    // O backend recusa violação de regra com 422 + mensagem explicando o quê (ex.:
    // apagar o CNPJ de uma alteração). Engolir isso num "tente novamente" genérico
    // faria o operador repetir a mesma ação até desistir, sem nunca saber o motivo.
    onError: (erro: unknown) => toast.error(detalheDoErro(erro)),
  });

  const avancarMutation = useMutation({
    mutationFn: async () => {
      await registrarComentarioPendente();
      await atualizarLegalizacao(legalizacao!.id, montarPatch());
      if (proxima) await moverLegalizacao(legalizacao!.id, { coluna_titulo: proxima });
    },
    onSuccess: () => {
      toast.success(`Avançou para ${proxima}`);
      invalidar();
      onClose();
    },
    // MESMO endpoint do salvar (PATCH /legalizacoes/{id}) — logo, mesmo 422 possível.
    // Mostrar o motivo num caminho e engolir no outro dá duas mensagens pro mesmo erro.
    onError: (erro: unknown) =>
      toast.error(detalheDoErro(erro, 'Não foi possível avançar. Tente novamente.')),
  });

  // Última etapa: salva antes de finalizar — é a marcação feita agora que libera o botão.
  const finalizarMutation = useMutation({
    mutationFn: async () => {
      await registrarComentarioPendente();
      await atualizarLegalizacao(legalizacao!.id, montarPatch());
      await finalizarLegalizacao(legalizacao!.id);
    },
    onSuccess: () => {
      toast.success('Processo finalizado');
      invalidar();
      queryClient.invalidateQueries({ queryKey: ['legalizacao-arquivados'] });
      setFinalizarAberto(false);
      onClose();
    },
    onError: (erro: unknown) =>
      toast.error(detalheDoErro(erro, 'Não foi possível finalizar. Tente novamente.')),
  });

  if (!legalizacao) return null;

  const setSubtype = (key: string, value: unknown) =>
    setSubtypeForm(prev => ({ ...prev, [key]: value === '' ? null : value }));
  const setEstado = (key: string, value: unknown) =>
    setEstadoEtapa(prev => ({ ...prev, [key]: value === '' ? null : value }));

  /**
   * Dispensa/reverte um item do checklist.
   *
   * Grava DENTRO do bloco da coluna, sob `_dispensas` — o mesmo espírito de `_obs` e
   * `_sla`, que já moram em `estado_etapas` sob chave prefixada. Nada de backend: o
   * `estado_etapas` é dict livre e o PATCH faz merge por chave de topo, e o bloco da
   * coluna sempre vai inteiro daqui.
   *
   * ⚠️ A marcação booleana é ZERADA junto. Um item dispensado que continuasse `true` no
   * estado cru apareceria marcado no board e dispensado no drawer — a divergência que
   * `itemSatisfeito` existe pra impedir. Um estado por vez.
   */
  const dispensarItem = (key: string, motivo: string, observacao: string) =>
    setEstadoEtapa(prev => ({
      ...prev,
      [key]: false,
      [CHAVE_DISPENSAS]: {
        ...dispensasDaEtapa(prev),
        [key]: {
          motivo,
          obs: observacao || undefined,
          em: new Date().toISOString(),
          por: usuarioNome || undefined,
        },
      },
    }));

  const reverterDispensa = (key: string) =>
    setEstadoEtapa(prev => {
      const restantes = { ...dispensasDaEtapa(prev) };
      delete restantes[key];
      return { ...prev, [CHAVE_DISPENSAS]: restantes };
    });
  /**
   * Marca/desmarca o aceite do cliente CARIMBANDO O HASH no mesmo ato.
   *
   * O hash tem que ser gravado aqui, no clique — não no `montarPatch`. `aprovacaoVigente`
   * exige o par (marca + hash do docx atual), então gravar só a marca deixava o item
   * impossível de concluir: o checkbox não marcava nem localmente, e como ele é `req`, o
   * "Avançar" ficava travado pra sempre numa coluna de índice 0, que não tem para onde
   * voltar. O `montarPatch` agora só REBAIXA aprovação que envelheceu.
   */
  const setAprovacaoCliente = (marcar: boolean) =>
    setEstadoEtapa(prev => {
      const novo: EstadoEtapa = { ...prev, [CHAVE_APROVACAO]: marcar };
      if (marcar && contratoEhFonteDaVerdade && hashAtual) {
        novo[CHAVE_APROVACAO_HASH] = hashAtual;
      } else {
        delete novo[CHAVE_APROVACAO_HASH];
      }
      return novo;
    });
  const toggleTipo = (t: TipoAlteracao) =>
    setTiposAlt(prev => (prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]));

  // Viabilidade COMPOSTA (multi-junta): os sub-passos vêm do registry (UF, município),
  // filtrados por tipo e pelos tipos_alteracao que dispararam a Rota A (persistidos em
  // "Definição + Rota"). Fora da viabilidade (ou UF não suportada), checklist estático.
  const registry = resolverRegistry(legalizacao.junta_uf, legalizacao.municipio);
  const tiposDoProcesso = tiposAlteracaoDoProcesso(legalizacao.estado_etapas);
  // Regime da TELA, não só o salvo: no Levantamento o select tira ou devolve os itens de contrato (MEI) na própria
  // etapa; com o salvo, trocar de MEI para outro regime e Avançar sem salvar passava sem contrato.
  const regimeDoProc = regimeDoProcesso(
    columnTitle
      ? { ...legalizacao.estado_etapas, [columnTitle]: estadoEtapa }
      : legalizacao.estado_etapas,
  );
  // Rota B numa etapa `somenteRotaA` (card legado, ou arrastado antes da regra): não se
  // aplica. Zera subpassos/checklist pra não travar o avanço; a etapa mostra o aviso.
  const etapaPuladaRotaB = etapaPuladaNaRota(tipo, columnTitle, rotaEfetiva, naoCliente);
  const subpassos =
    !etapaPuladaRotaB && etapa?.viabilidadeComposta && registry
      ? subpassosAplicaveis(registry, { tipoLegalizacao: tipo, tiposAlteracao: tiposDoProcesso })
      : null;
  // Fail-open sinalizado: alteração com tipos indeterminados mostrou subpasso condicional.
  const subpassosRevisar =
    !etapaPuladaRotaB &&
    !!etapa?.viabilidadeComposta &&
    viabilidadeRevisar(registry, { tipoLegalizacao: tipo, tiposAlteracao: tiposDoProcesso });
  const checklistEfetivo = etapaPuladaRotaB
    ? []
    : checklistEfetivoDaEtapa(
        tipo,
        columnTitle,
        legalizacao.junta_uf,
        legalizacao.municipio,
        tiposDoProcesso,
        regimeDoProc,
      );

  // Para as chaves DERIVADAS o carimbo em `estado_etapas` é só um CACHE (existe pro board e
  // pro drag-gate, que leem o estado cru e não buscam o contrato por card). A verdade é o
  // contrato. Enquanto isto era um OR, o cache vencia a verdade: depois de uma regeração —
  // que zera a vistoria no backend — o item continuava verde até alguém salvar a etapa de
  // novo. Com o estado do contrato conhecido, o derivado MANDA; sem ele (query em voo), cai
  // no carimbo pra não piscar falso-negativo.
  const doneEfetivo = (key: string): boolean => {
    // A pergunta da exigência não tem dispensa: vale a resposta, como no quadro e no arraste.
    if (key === CHAVE_EXIGENCIA || key === CHAVE_SIMPLES || key === CHAVE_CLIENTE)
      return itemSatisfeito(estadoEtapa, key);
    // ⚠️ A DISPENSA VEM PRIMEIRO, na frente dos dois atalhos abaixo.
    //
    // Os atalhos existem pra que a verdade do CONTRATO vença um carimbo velho em
    // `estado_etapas`. Mas dispensa não é carimbo velho: é decisão explícita, com motivo
    // e autor. Quando ela ficava atrás dos atalhos, dispensar `auditoria_ok` ou
    // `cliente_aprovou` deixava a linha "DISPENSADO" e o Avançar travado — enquanto o
    // gate do ARRASTE, que lê o estado cru, destravava. Dois gates discordando sobre o
    // mesmo card, e o mais permissivo era o que não pedia confirmação.
    if (dispensaDoItem(estadoEtapa, key)) return true;
    if (rotaPelosTipos(key)) return !!rotaResultado.rota;
    if (key === CHAVE_APROVACAO) return aprovacaoVigente(estadoEtapa);
    if (CHAVES_DERIVADAS.includes(key) && contratoEhFonteDaVerdade)
      return doneDerivadoContrato(key);
    return itemSatisfeito(estadoEtapa, key) || doneDerivadoContrato(key);
  };
  /** A aprovação existe mas é de uma versão anterior do documento (regeração mudou o
   *  conteúdo). Serve pra tela explicar por que o item desmarcou sozinho. */
  const aprovacaoDesatualizada = !!estadoEtapa[CHAVE_APROVACAO] && !aprovacaoVigente(estadoEtapa);

  // Gate: todos os itens obrigatórios do checklist EFETIVO da etapa satisfeitos.
  const obrigatorios = checklistEfetivo.filter(c => c.req);
  const faltam = obrigatorios.filter(c => !doneEfetivo(c.key)).length;
  // Fail-CLOSED: coluna sem bloco no PLAYBOOK (título do backend saiu de sincronia com o
  // front) não destrava o avanço. Antes o checklist vinha vazio e o botão liberava.
  const podeAvancar = faltam === 0 && etapaConfigurada(tipo, columnTitle);
  let iconeDoAvanco = <LockSimple size={16} />;
  if (podeAvancar)
    iconeDoAvanco = proxima ? <ArrowRight size={16} /> : <CircleWavyCheck size={16} />;
  // Mesma função que o CARD do board usa pra dizer o que falta — uma fonte só, então o
  // rodapé do modal nunca contradiz o card de onde ele foi aberto.

  // Identificadores da empresa no cabeçalho — só os preenchidos. Ordem: do mais
  // identificador (CNPJ) para o mais contextual (município). Todos com rótulo: "JUCEG/GO"
  // sozinho era lido como dado da empresa.
  const identificadores = [
    { rotulo: 'CNPJ', valor: formatarCnpj(legalizacao.cnpj), copiavel: true },
    { rotulo: 'NIRE', valor: legalizacao.nire, copiavel: true },
    { rotulo: 'Protocolo', valor: protocoloDoProcesso(legalizacao), copiavel: true },
    {
      rotulo: 'Junta',
      valor: [legalizacao.junta_sigla, legalizacao.junta_uf].filter(Boolean).join('/') || null,
      copiavel: false,
    },
    { rotulo: 'Município', valor: legalizacao.municipio, copiavel: false },
  ].filter((i): i is { rotulo: string; valor: string; copiavel: boolean } => !!i.valor);

  const copiar = (valor: string, rotulo: string) => {
    navigator.clipboard
      .writeText(valor)
      .then(() => toast.success(`${rotulo || 'Valor'} copiado`))
      .catch(() => toast.error('Não foi possível copiar'));
  };

  // Os campos `display` não vão pra grade de inputs — são procedência, não formulário. Os da UF: sem os de DBE
  // onde a abertura não tem DBE (salvo o que já foi preenchido); e sem os que só o "Iniciar aqui" pede.
  const camposDaUf = new Set(
    camposEfetivosDaEtapa(tipo, columnTitle, legalizacao.junta_uf, legalizacao.municipio).map(
      c => c.key,
    ),
  );
  const preenchido = (c: PlaybookCampo) => {
    const v = c.origem === 'subtype' ? subtypeForm[c.key] : estadoEtapa[c.key];
    return v != null && v !== '';
  };
  const camposDaEtapa = (etapa?.campos ?? []).filter(
    c => !c.soNoIniciarAqui && (camposDaUf.has(c.key) || preenchido(c)),
  );
  const camposProcedencia = camposDaEtapa.filter(c => c.origem === 'display');
  const camposEditaveis = camposDaEtapa.filter(c => c.origem !== 'display');
  const linkDaColuna = linkDaEtapa(tipo, columnTitle, legalizacao.junta_uf, legalizacao.municipio);

  // O "Falta" lê o estado, e a "Rota definida" sai dos tipos do bloco (`itemSatisfeito`): os da tela entram nele já
  // antes de salvar (o salvar grava os mesmos).
  const estadoComRota = (etapa?.checklist ?? []).some(c => rotaPelosTipos(c.key))
    ? { ...estadoEtapa, tipos_alteracao: tiposAlt }
    : estadoEtapa;
  const acaoPendente = proximaAcao(tipo, columnTitle, estadoComRota, {
    uf: legalizacao.junta_uf,
    municipio: legalizacao.municipio,
    tiposAlteracao: tiposDoProcesso,
    regime: regimeDoProc,
  });

  /**
   * "Houve exigência?" no lugar do checkbox (pedido da Juliana, 14/10). "Não" marca a resposta do órgão;
   * "Sim" pede o texto, que é o que o card do quadro mostra enquanto o órgão não defere.
   */
  const renderExigencia = (chk: { key: string; label: string }) => {
    const resposta = estadoEtapa[CHAVE_EXIGENCIA];
    const texto = estadoEtapa[CHAVE_EXIGENCIA_TEXTO];
    const respondida = itemSatisfeito(estadoEtapa, chk.key);
    let cores = { borda: 'gray.100', fundo: 'white' };
    if (resposta === 'Sim') cores = { borda: 'orange.200', fundo: 'orange.50' };
    else if (respondida) cores = { borda: 'green.200', fundo: 'green.50' };
    const responder = (r: RespostaExigencia) =>
      setEstadoEtapa(prev =>
        responderExigencia(prev, r, respostasDoOrgao(tipo, columnTitle, checklistEfetivo)),
      );
    // "Não" sozinho seria lido como "ainda não houve" antes de o órgão responder, e marcaria o Deferido.
    const deferido = etapa?.viabilidadeComposta ? 'deferida' : 'deferido';
    const rotulo = (r: RespostaExigencia) => (r === 'Não' ? `Não, ${deferido}` : r);
    return (
      <Box
        key={chk.key}
        p={2.5}
        borderWidth="1px"
        borderColor={cores.borda}
        bg={cores.fundo}
        borderRadius="lg"
      >
        <Flex align="center" gap={3}>
          <Text flex={1} fontSize="13px" color="gray.700">
            {chk.label}
          </Text>
          <ButtonGroup size="xs" isAttached aria-label={chk.label}>
            {RESPOSTAS_EXIGENCIA.map(r => (
              <Button
                key={r}
                minW="44px"
                fontSize="12px"
                // O tema pinta solid/outline na cor da marca (ignora colorScheme); o fundo da linha
                // é que diz o estado: laranja com exigência, verde sem. O hover do `solid` do tema é
                // branco: sem o `_hover`, a resposta escolhida parecia desfeita sob o mouse.
                variant={resposta === r ? 'solid' : 'outline'}
                _hover={resposta === r ? { bg: 'primary', color: 'white' } : undefined}
                aria-pressed={resposta === r}
                onClick={() => responder(r)}
              >
                {rotulo(r)}
              </Button>
            ))}
          </ButtonGroup>
          {respondida ? (
            <CheckCircle size={15} weight="fill" color="var(--chakra-colors-green-500)" />
          ) : (
            <Badge fontSize="10px" colorScheme="orange" borderRadius="full" px={2}>
              obrigatório
            </Badge>
          )}
        </Flex>
        {resposta === 'Sim' && (
          <>
            <Textarea
              mt={2}
              value={typeof texto === 'string' ? texto : ''}
              onChange={e => setEstado(CHAVE_EXIGENCIA_TEXTO, e.target.value)}
              placeholder="O que o órgão pediu? (obrigatório)"
              aria-label="Exigência"
              size="sm"
              fontSize="13px"
              rows={3}
              bg="white"
              borderRadius="lg"
              isInvalid={!textoDaExigencia(estadoEtapa)}
            />
            <Text fontSize="11px" color="gray.500" mt={1}>
              Quando o órgão deferir, marque abaixo; a exigência continua registrada.
            </Text>
          </>
        )}
      </Box>
    );
  };

  /**
   * Pergunta Sim/Não, sem marcar nada sozinho. "Simples Nacional deferido?" (19/10, Camila): Sim, ou Não com o
   * motivo. "Será cliente da Especialistas?" (27/10, Juliana): Não pula "Demais órgãos" e o Licenciamento; sem motivo nem dispensa.
   */
  const renderSimNao = (chk: { key: string; label: string }) => {
    const ehSimples = chk.key === CHAVE_SIMPLES;
    const resposta = estadoEtapa[chk.key];
    const motivo = estadoEtapa[CHAVE_SIMPLES_MOTIVO];
    const respondida = itemSatisfeito(estadoEtapa, chk.key);
    const dispensa = dispensaDoItem(estadoEtapa, chk.key);
    let cores = { borda: 'gray.100', fundo: 'white' };
    if ((ehSimples && resposta === 'Não') || dispensa)
      cores = { borda: 'orange.200', fundo: 'orange.50' };
    else if (respondida) cores = { borda: 'green.200', fundo: 'green.50' };
    return (
      <Box
        key={chk.key}
        p={2.5}
        borderWidth="1px"
        borderColor={cores.borda}
        bg={cores.fundo}
        borderRadius="lg"
      >
        <Flex align="center" gap={3}>
          <Text flex={1} fontSize="13px" color="gray.700">
            {chk.label}
          </Text>
          <ButtonGroup size="xs" isAttached aria-label={chk.label}>
            {RESPOSTAS_SIMPLES.map(r => (
              <Button
                key={r}
                minW="44px"
                fontSize="12px"
                variant={resposta === r ? 'solid' : 'outline'}
                _hover={resposta === r ? { bg: 'primary', color: 'white' } : undefined}
                aria-pressed={resposta === r}
                onClick={() => setEstado(chk.key, r)}
              >
                {r}
              </Button>
            ))}
          </ButtonGroup>
          {/* Dispensa (processo antigo sem regime, por exemplo): o regime não se edita mais nesta etapa. */}
          {dispensa && (
            <Tooltip hasArrow placement="top" label="Voltar a cobrar a resposta">
              <Button
                size="xs"
                variant="ghost"
                colorScheme="orange"
                fontSize="11px"
                borderRadius="full"
                px={2}
                onClick={() => reverterDispensa(chk.key)}
              >
                dispensado
              </Button>
            </Tooltip>
          )}
          {ehSimples && !dispensa && !respondida && (
            <Button
              size="xs"
              variant="ghost"
              colorScheme="orange"
              fontSize="11px"
              fontWeight="500"
              borderRadius="full"
              px={2}
              onClick={() => setDispensando({ key: chk.key, label: chk.label })}
            >
              dispensar
            </Button>
          )}
          {respondida && !dispensa && (
            <CheckCircle size={15} weight="fill" color="var(--chakra-colors-green-500)" />
          )}
          {!respondida && (
            <Badge fontSize="10px" colorScheme="orange" borderRadius="full" px={2}>
              obrigatório
            </Badge>
          )}
        </Flex>
        {dispensa && (
          <Text fontSize="11px" color="orange.700" mt={1} noOfLines={1}>
            {rotuloDoMotivo(dispensa.motivo)}
            {dispensa.obs ? ` — ${dispensa.obs}` : ''}
          </Text>
        )}
        {!ehSimples && resposta === 'Não' && (
          <Text fontSize="11px" color="gray.500" mt={1}>
            Não será cliente: o processo pula Demais órgãos e o Licenciamento.
          </Text>
        )}
        {ehSimples && resposta === 'Não' && (
          <>
            <Textarea
              mt={2}
              value={typeof motivo === 'string' ? motivo : ''}
              onChange={e => setEstado(CHAVE_SIMPLES_MOTIVO, e.target.value)}
              placeholder="Motivo: o que a Receita apontou (obrigatório)"
              aria-label="Motivo do Simples indeferido"
              size="sm"
              fontSize="13px"
              rows={3}
              bg="white"
              borderRadius="lg"
              isInvalid={!motivoDoSimples(estadoEtapa)}
            />
            <Text fontSize="11px" color="gray.500" mt={1}>
              Há 30 dias da inscrição do CNPJ para regularizar ou impugnar. Deferido depois, troque
              para Sim.
            </Text>
          </>
        )}
      </Box>
    );
  };

  const renderCheckRow = (chk: { key: string; label: string; req: boolean; url?: string }) => {
    if (chk.key === CHAVE_EXIGENCIA) return renderExigencia(chk);
    if (chk.key === CHAVE_SIMPLES || chk.key === CHAVE_CLIENTE) return renderSimNao(chk);
    const auto = doneDerivadoContrato(chk.key) || (rotaPelosTipos(chk.key) && !!rotaResultado.rota);
    const done = doneEfetivo(chk.key);
    const dispensa = dispensaDoItem(estadoEtapa, chk.key);
    // Três estados excludentes na mesma linha (dispensado / feito / pendente). Nomear
    // o estado uma vez evita repetir a mesma cadeia de condições em cada prop de estilo
    // — e o lint proíbe ternário aninhado justamente por isso.
    const tomDaLinha: 'dispensado' | 'feito' | 'pendente' = (() => {
      if (dispensa) return 'dispensado';
      return done ? 'feito' : 'pendente';
    })();
    const CORES_DA_LINHA = {
      dispensado: {
        borda: 'orange.200',
        fundo: 'orange.50',
        hover: 'orange.100',
        texto: 'orange.800',
      },
      feito: { borda: 'green.200', fundo: 'green.50', hover: 'green.100', texto: 'green.700' },
      pendente: { borda: 'gray.100', fundo: 'white', hover: 'gray.50', texto: 'gray.700' },
    } as const;
    const cores = CORES_DA_LINHA[tomDaLinha];
    // Aceite do cliente com contrato vinculado mas query ainda em voo: marcar agora
    // gravaria a aprovação SEM hash, e aí board (estado cru → verde) e drawer
    // (`aprovacaoVigente` → falso) discordariam sobre o mesmo card. Espera resolver.
    // A rota sem tipos marcados também espera: quem resolve é marcar os tipos acima, não o item.
    const travado =
      (chk.key === CHAVE_APROVACAO && !!contratoVinculadoId && !contratoCarregou) ||
      (rotaPelosTipos(chk.key) && !rotaResultado.rota);
    /**
     * Este item aceita dispensa?
     *
     * `!done` — dispensar o que já está feito não quer dizer nada. Quem marcou por
     * engano desmarca primeiro. (Visto na tela: "CNPJ emitido", verde e riscado, exibia
     * o botão ao lado.)
     *
     * `!auto` — item que o contrato JÁ satisfaz não precisa de dispensa; o `montarPatch`
     * recarimba a chave a cada save e a dispensa seria ruído.
     *
     * ⚠️ `doc_gerado`, `auditoria_ok` e `cliente_aprovou` FICAM dispensáveis, por decisão
     * da operação (2026-08-28) e com um caso concreto: o gerador não cobre todo ato —
     * MEI → S.A., por exemplo. Aí o documento vem de fora, e a auditoria de fidelidade
     * nem tem sujeito (sem render nosso, não há o que comparar com o vigente).
     *
     * Isso exigiu `doneEfetivo` consultar a dispensa ANTES dos atalhos do contrato.
     * Enquanto não consultava, dispensar essas chaves criava DOIS defeitos: a linha
     * ficava "DISPENSADO" com o Avançar travado (drawer ignorava), e o gate do ARRASTE
     * — que lê o estado cru — destravava. Dava pra empurrar o card sem a auditoria.
     */
    const podeDispensar = !auto && !travado && !done;
    // Item derivado do contrato (auto) → só-leitura + selo "automático". Senão, o
    // clássico: feito (check verde) ou obrigatório pendente.
    const renderRight = () => {
      // A aprovação do cliente some sozinha quando o documento é regerado com conteúdo
      // diferente. Sem dizer isso, o operador acha que perdeu a marcação por bug.
      if (chk.key === CHAVE_APROVACAO && aprovacaoDesatualizada)
        return (
          <Tooltip
            hasArrow
            label="O documento foi gerado de novo depois desta aprovação, e o conteúdo mudou. O cliente precisa aprovar a versão atual."
          >
            <Badge fontSize="10px" colorScheme="orange" borderRadius="full" px={2}>
              reaprovar
            </Badge>
          </Tooltip>
        );
      // ANTES do `done`: dispensado TAMBÉM satisfaz o item, então cairia no check verde
      // e o operador leria "feito" onde na verdade foi "não se aplica". São estados
      // diferentes e a tela tem que distinguir.
      if (dispensa)
        return (
          <Tooltip
            hasArrow
            placement="top"
            label={`${rotuloDoMotivo(dispensa.motivo)}${dispensa.obs ? ` — ${dispensa.obs}` : ''}${
              dispensa.por ? ` · ${dispensa.por}` : ''
            }`}
          >
            <Badge fontSize="10px" colorScheme="orange" borderRadius="full" px={2}>
              dispensado
            </Badge>
          </Tooltip>
        );
      if (auto)
        return (
          <Badge fontSize="10px" colorScheme="purple" borderRadius="full" px={2}>
            automático
          </Badge>
        );
      if (done)
        return <CheckCircle size={15} weight="fill" color="var(--chakra-colors-green-500)" />;
      if (chk.req)
        return (
          <Badge fontSize="10px" colorScheme="orange" borderRadius="full" px={2}>
            obrigatório
          </Badge>
        );
      return null;
    };
    return (
      <Flex
        key={chk.key}
        align="center"
        gap={3}
        p={2.5}
        borderWidth="1px"
        borderColor={cores.borda}
        bg={cores.fundo}
        borderRadius="lg"
        cursor={auto || travado ? 'default' : 'pointer'}
        opacity={travado ? 0.6 : undefined}
        transition="all .12s"
        _hover={auto || travado ? undefined : { bg: cores.hover }}
        onClick={(() => {
          if (auto || travado) return undefined;
          // Dispensado: clicar na linha REVERTE (volta a ser cobrado). Deixar o toggle
          // normal marcaria o booleano por baixo de uma dispensa ainda gravada, e o item
          // ficaria nos dois estados ao mesmo tempo.
          if (dispensa) return () => reverterDispensa(chk.key);
          if (chk.key === CHAVE_APROVACAO) return () => setAprovacaoCliente(!done);
          return () => setEstadoEtapa(prev => marcarItem(prev, chk.key, !done));
        })()}
      >
        <Checkbox
          isChecked={done && !dispensa}
          colorScheme="green"
          pointerEvents="none"
          onChange={() => {}}
        />
        <Box flex={1} minW={0}>
          <Text
            fontSize="13px"
            color={cores.texto}
            textDecoration={done && !dispensa ? 'line-through' : undefined}
          >
            {chk.label}
          </Text>
          {/* O motivo fica NA LINHA, não só no tooltip: o problema que a dispensa
              resolve é justamente alguém abrir o card depois e não saber por que o item
              está assim. Esconder a resposta atrás de hover devolveria a ambiguidade. */}
          {dispensa && (
            <Text fontSize="11px" color="orange.700" noOfLines={1}>
              {rotuloDoMotivo(dispensa.motivo)}
              {dispensa.obs ? ` — ${dispensa.obs}` : ''}
            </Text>
          )}
        </Box>
        {/* Link do órgão (2026-08-06, pedido da Juliana): abre o portal em nova aba.
            `stopPropagation` é obrigatório — a linha inteira é o toggle do check, e
            sem isso clicar no link marcaria/desmarcaria o item junto. */}
        {chk.url && (
          <Tooltip label={chk.url} placement="top" hasArrow openDelay={400}>
            <Link
              href={chk.url}
              isExternal
              onClick={e => e.stopPropagation()}
              display="inline-flex"
              alignItems="center"
              gap={1}
              fontSize="11px"
              fontWeight="500"
              color="blue.600"
              _hover={{ color: 'blue.700', textDecoration: 'underline' }}
            >
              abrir
              <ArrowSquareOut size={13} weight="bold" />
            </Link>
          </Tooltip>
        )}
        {/* `stopPropagation` obrigatório: a linha inteira é o toggle do check — sem isto o
            clique em "dispensar" marcaria o item junto de abrir o modal. Mesmo motivo do
            link do órgão acima. */}
        {podeDispensar && (
          <Tooltip
            hasArrow
            placement="top"
            openDelay={400}
            label="Não se aplica a este processo? Registre o motivo em vez de deixar em branco."
          >
            <Button
              size="xs"
              variant="ghost"
              colorScheme="orange"
              fontSize="11px"
              fontWeight="500"
              borderRadius="full"
              px={2}
              onClick={e => {
                e.stopPropagation();
                setDispensando({ key: chk.key, label: chk.label });
              }}
            >
              dispensar
            </Button>
          </Tooltip>
        )}
        {renderRight()}
      </Flex>
    );
  };

  const acaoOp = etapa?.acao?.tipoOperacao ?? null;
  const contratoId = legalizacao.contrato_societario_id;
  // sem | edicao (rascunho, em revisão) | pronto (gerado) | fora (arquivado) | sumiu | erro |
  // carregando
  const estadoContrato = estadoDoContrato(
    contratoId,
    contratoVinculado,
    contratoSumiu,
    falhouLeituraDoContrato,
  );
  const temContrato = estadoContrato !== 'sem' && estadoContrato !== 'sumiu';
  // MEI não tem contrato social (16/10): sem contrato vinculado, gerar/vincular dá lugar a um aviso. Com contrato
  // vinculado (ou que sumiu, com o aviso e o caminho para limpar o vínculo) a caixa fica.
  const semContratoPorMei = regimeDoProc === 'MEI' && estadoContrato === 'sem';

  // Sem await antes do `window.open`: o clique ainda é o gesto do usuário. Ao voltar para
  // esta aba, o card recarrega (vínculo e estado do contrato).
  const abrirEmAba = (url: string) => {
    foiAoContratoRef.current = true;
    window.open(url, '_blank');
  };
  // Gerado → LISTAGEM com o contrato em destaque (visualizar/baixar/vistoria), em vez de
  // reabrir o gerador na Revisão — evita regeração acidental. Em edição → o gerador retoma
  // de onde parou, ainda ligado a este processo.
  const abrirContrato = () => contratoId && abrirEmAba(urlDaListagem(contratoId));
  const continuarContrato = () =>
    contratoId && abrirEmAba(urlParaContinuar(contratoId, legalizacao.id));
  const desvincularContrato = () =>
    showDialog({
      status: 'warning',
      title: `Desvincular o contrato #${contratoId}?`,
      description:
        'O contrato continua no sistema; só deixa de valer para este card. Dá para vincular de novo depois.',
      confirmLabel: 'Desvincular',
      cancelLabel: 'Cancelar',
      onConfirm: () => vincularMutation.mutate(null),
    });

  const gerarContrato = async () => {
    foiAoContratoRef.current = true;
    if (!acaoOp) return;
    /**
     * ABA NOVA, aberta ANTES do await — e não depois.
     *
     * O navegador só deixa `window.open` passar quando ele acontece dentro do gesto do
     * usuário. Depois de um `await`, o clique já não é mais o "motivo" da chamada e o
     * bloqueador de pop-up derruba a aba, em silêncio: o operador clicaria em "gerar" e
     * não aconteceria nada. Então a aba é aberta em branco agora e recebe o endereço
     * quando o save termina.
     */
    const aba = window.open('', '_blank');
    // SALVA antes de navegar: ao voltar do gerador o card recarrega do servidor (vínculo do
    // contrato), e o que estivesse só na tela — marcas, campos, os tipos de alteração — se
    // perderia. (O gerador NÃO lê o processo: empresa e tipos são informados de novo lá.)
    try {
      // Vinculado que sumiu: o gerador vai ligar outro — zera o que vinha do antigo e solta
      // o vínculo morto (o gerador não troca contrato de processo já vinculado).
      await salvarMutation.mutateAsync(
        estadoContrato === 'sumiu' ? { contratoMudou: true, desvincular: true } : undefined,
      );
    } catch {
      // O toast de erro já vem do onError da mutation. Fecha a aba em branco: deixá-la
      // aberta mostraria uma página vazia como se o "gerar" tivesse funcionado.
      aba?.close();
      return;
    }
    const destino = `/societario/contratos/criar?tipo=${acaoOp}&legalizacaoId=${legalizacao.id}`;
    // Pop-up bloqueado mesmo assim (extensão, política do navegador) → `aba` é null.
    // Cai pra navegação normal em vez de engolir o clique.
    if (aba) aba.location.href = destino;
    else navigate(destino);
  };

  /**
   * Faixa de PROCEDÊNCIA — campos `origem: 'display'`.
   *
   * Eles não são campo: dizem DE ONDE o dado vem ("Sócios · Coletados no wizard"), e não
   * há nada pra digitar. Dentro da grade, com rótulo igual ao dos inputs e um texto
   * cinza itálico embaixo, liam como input vazio ou desabilitado — o operador procurava
   * onde clicar. Fora da grade, respondem a outra pergunta e param de competir.
   */
  const renderProcedencia = (campos: PlaybookCampo[]) => (
    <Flex
      gap={2.5}
      align="flex-start"
      mb={3}
      px={3}
      py={2.5}
      borderRadius="lg"
      bg="rgba(15,23,42,0.03)"
      borderWidth="1px"
      borderColor="gray.100"
    >
      <Box color="gray.400" flexShrink={0} pt="2px">
        <Info size={14} weight="fill" />
      </Box>
      <Flex direction="column" gap={1} minW={0}>
        {campos.map(c => (
          <Text key={c.key} fontSize="11px" color="gray.500" lineHeight="1.45">
            <Text as="span" fontWeight="700" color="gray.700">
              {c.label}
            </Text>{' '}
            — {c.valorDisplay ?? '—'}
          </Text>
        ))}
      </Flex>
    </Flex>
  );

  const renderCampo = (c: PlaybookCampo) => {
    if (c.origem === 'tipos') {
      return (
        <Box key={c.key} gridColumn="1 / -1">
          <FormLabel fontSize="11px" fontWeight="700" color="gray.700">
            {c.label}
          </FormLabel>
          <Wrap spacing={2}>
            {TIPOS_ALT_OPCOES.map(op => {
              const on = tiposAlt.includes(op.value);
              return (
                <WrapItem key={op.value}>
                  <Button
                    size="xs"
                    fontSize="13px"
                    variant={on ? 'solid' : 'outline'}
                    colorScheme={on ? 'purple' : 'gray'}
                    onClick={() => toggleTipo(op.value)}
                    borderRadius="full"
                  >
                    {op.label}
                  </Button>
                </WrapItem>
              );
            })}
          </Wrap>
          {rotaResultado.rota && (
            <Text fontSize="11px" color={rotaResultado.revisar ? 'orange.500' : 'gray.500'} mt={2}>
              → Rota {rotaResultado.rota}: {rotaResultado.motivo}
            </Text>
          )}
        </Box>
      );
    }
    // 'subtype' ou 'estado' → input editável.
    const val = c.origem === 'subtype' ? subtypeForm[c.key] : estadoEtapa[c.key];
    const onChange = (entrada: string) => {
      /**
       * Máscara só quando o número está COMPLETO.
       *
       * `formatarCnpj` mascara com exatamente 14 dígitos e devolve verbatim qualquer
       * outra coisa — então digitar não reformata a cada tecla (nada de cursor pulando),
       * e apagar um dígito volta ao texto livre em vez de brigar com quem edita. Colar um
       * CNPJ cru formata na hora.
       *
       * Guarda o MASCARADO, e isso é seguro: o backend compara por `_norm_cnpj`
       * (alfanuméricos), a busca da listagem usa `soAlfaNum`, e cabeçalho e card já
       * passavam por `formatarCnpj`. O próprio backend registra que "mascarado e cru
       * convivem no banco" — o que muda é que daqui pra frente converge pro mascarado.
       */
      const v = c.mascara === 'cnpj' ? (formatarCnpj(entrada) ?? entrada) : entrada;
      if (c.origem === 'subtype') setSubtype(c.key, v);
      else setEstado(c.key, v);
      // Auto-marca o item de checklist que mapeia 1:1 a este campo, ao preenchê-lo.
      if (c.marca && v.trim()) setEstado(c.marca, true);
    };
    const inputType = c.tipo === 'date' || c.tipo === 'number' ? c.tipo : 'text';
    const label = rotuloComRegistry(c, legalizacao.junta_uf, legalizacao.municipio);
    // Lista: o texto livre gravado antes dela continua aparecendo até alguém escolher.
    const atual = typeof val === 'string' ? val : '';
    const opcoes =
      atual && !(c.opcoes ?? []).includes(atual) ? [...(c.opcoes ?? []), atual] : (c.opcoes ?? []);
    return (
      <FormControl key={c.key}>
        {/* Rótulo MENOR que o campo, não maior. A `sm` do Chakra (14px, peso médio)
            deixava o rótulo mais pesado que o próprio input — hierarquia invertida: o
            campo é o objeto, o rótulo é a etiqueta. Escuro (gray.700) e não cinza
            claro: o problema era o TAMANHO, não faltar contraste.
            A 11px, rótulos como "Inscrição imobiliária (IPTU)" cabem em uma linha e a
            grade para de escalonar. */}
        <FormLabel fontSize="11px" fontWeight="700" color="gray.700" mb={1} lineHeight="1.35">
          {label}
        </FormLabel>
        {c.tipo === 'select' ? (
          <Select
            value={atual}
            onChange={e => onChange(e.target.value)}
            placeholder={c.placeholder}
            borderRadius="lg"
            size="sm"
            // Altura e recuo do Input `sm` do tema: sem eles a lista fica mais baixa que os campos
            // da mesma linha e o rótulo desce. O recuo vai pelo `sx`: `ps` cairia no invólucro.
            h="40px"
            sx={{ paddingInlineStart: 6 }}
            fontSize="13px"
            bg="white"
          >
            {opcoes.map(o => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </Select>
        ) : (
          <Input
            value={
              // Processo antigo tem CNPJ cru gravado. Sem mascarar aqui, ele continuaria
              // divergindo do cabeçalho até alguém reeditar o campo.
              (c.mascara === 'cnpj' ? formatarCnpj(val as string) : (val as string)) ?? ''
            }
            onChange={e => onChange(e.target.value)}
            placeholder={c.placeholder}
            type={inputType}
            borderRadius="lg"
            size="sm"
            fontSize="13px"
            bg="white"
            _placeholder={{ color: 'gray.400', fontSize: '13px' }}
          />
        )}
      </FormControl>
    );
  };

  // Peek read-only de uma etapa JÁ concluída (aberto pelo stepper).
  // Campos + checklist de uma etapa, so' leitura. O corpo virou o componente
  // `EtapaReadOnly` porque a lista de ARQUIVADOS precisa do mesmo render e nao
  // tem card ativo — tudo sai de `legalizacao.estado_etapas`.
  const renderReadOnly = (col: string) => (
    <EtapaReadOnly legalizacao={legalizacao} tipo={tipo} coluna={col} />
  );

  // Fechar recarrega a listagem: a faixa do card no quadro mostra o último comentário, e
  // comentar pela aba não passa pelo PATCH da legalização. (Recarregar com o modal ABERTO
  // reiniciaria o formulário da etapa — o modal relê a legalização da listagem.)
  const fechar = () => {
    invalidar();
    onClose();
  };

  // SÓ em constituição (decisão do usuário, 2026-08-17): ali o nome é PRETENDIDO e a
  // viabilidade pode reprová-lo. Em alteração e baixa a razão social é o nome REGISTRADO.
  // Por isso o lápis genérico do modal fica desligado e este entra no lugar dele.
  const renomearAcao =
    card?.id && tipo === 'constituicao' ? (
      <Tooltip hasArrow label="Alterar o nome pretendido (a viabilidade pode ter reprovado)">
        <IconButton
          aria-label="Alterar nome da empresa"
          icon={<NotePencil size={16} weight="bold" />}
          size="sm"
          variant="ghost"
          borderRadius="lg"
          color="gray.400"
          _hover={{ color: 'purple.600', bg: 'purple.50' }}
          flexShrink={0}
          onClick={() => setRenomearAberto(true)}
        />
      </Tooltip>
    ) : null;

  // A observação de antes dos comentários (texto único, sem autor nem data) fecha a lista, que
  // corre da mais nova para a mais antiga. Sem autor gravado, leva o nome do responsável do
  // processo (decisão do usuário, 02/10) — que pode não ser quem a escreveu.
  const obsAntiga = obsDoCard(legalizacao.estado_etapas);
  const obsAnteriorAosComentarios = obsAntiga.trim() ? (
    <Flex gap={2.5} mb={3}>
      {respNome ? (
        <Avatar size="xs" name={respNome} mt={0.5} />
      ) : (
        <Flex
          w="24px"
          h="24px"
          mt={0.5}
          borderRadius="full"
          bg="gray.100"
          align="center"
          justify="center"
          flexShrink={0}
        >
          <Note size={13} color="#94A3B8" weight="duotone" />
        </Flex>
      )}
      <Box flex={1} minW={0}>
        {respNome ? (
          <Flex alignItems="baseline" gap={2}>
            <Text fontSize="12px" fontWeight="600" color="gray.700">
              {respNome}
            </Text>
            <Text fontSize="10px" color="gray.400">
              observação anterior
            </Text>
          </Flex>
        ) : (
          <Text fontSize="12px" fontWeight="600" color="gray.600">
            Observação anterior{' '}
            <Text as="span" fontWeight="400" color="gray.500">
              · sem autor registrado
            </Text>
          </Text>
        )}
        <Text
          fontSize="13px"
          color="gray.600"
          mt={0.5}
          bg="gray.50"
          p={2}
          borderRadius="lg"
          whiteSpace="pre-wrap"
          wordBreak="break-word"
        >
          {obsAntiga}
        </Text>
      </Box>
    </Flex>
  ) : null;

  // Aba "Etapa": o que se FAZ nesta etapa — ação, campos e checklist — e o que já foi
  // coletado nas anteriores. A trilha e os atalhos ficam no cabeçalho, acima das abas.
  const abaEtapa = (
    <Stack spacing={4} pb={2}>
      <Box borderWidth="1px" borderColor="gray.100" borderRadius="xl" p={4}>
        {/* O título "Nesta etapa" só entra quando o card tem conteúdo PRÓPRIO
                        — ação, campos ou nota. Nas 10 etapas sem campo nem ação (ex.:
                        Assinatura), ele aparecia com NADA embaixo, colado no "Checklist da
                        etapa": dois títulos empilhados anunciando um vazio. Numa rodada
                        anterior tirei o texto "Sem campos próprios" e esqueci o título,
                        que era o que de fato ocupava a linha. */}
        {/* ⚠️ `nota` SAIU da condição.
                        O comentário acima vale mais do que nunca: o título só entra com
                        conteúdo PRÓPRIO. Enquanto a nota era uma CAIXA, ela contava como
                        conteúdo; agora que virou o "?" do próprio título, contar de novo
                        reabriria o defeito — em 6 das 28 etapas ("Demais órgãos" nas duas,
                        e 4 da baixa) o título apareceria com NADA embaixo, colado no
                        "Checklist da etapa". Foi o que aconteceu, e só apareceu numa
                        conferência depois de eu já ter aberto o PR.

                        Como algumas dessas 6 etapas têm nota que importa (a de "Demais
                        órgãos" fala do que sai junto do deferimento), o "?" não some: ele
                        ancora na PRIMEIRA seção que de fato renderiza — aqui, ou no
                        checklist logo abaixo. */}
        {(etapa?.acao || etapa?.campos?.length) && (
          <SectionTitle icon={NotePencil} accent="#7C3AED" ajuda={etapa?.nota}>
            Nesta etapa
          </SectionTitle>
        )}
        {etapa?.acao && semContratoPorMei && (
          <Text
            fontSize="13px"
            color="gray.600"
            bg="gray.50"
            borderRadius="lg"
            px={3.5}
            py={3}
            mb={etapa.campos?.length ? 3.5 : 0}
          >
            MEI não tem contrato social: nesta etapa não há contrato a gerar.
          </Text>
        )}
        {etapa?.acao && !semContratoPorMei && (
          /* Borda TRACEJADA saiu: neste admin tracejado significa "vaga
                         vazia, clique pra preencher" — é o que o "Iniciar aqui" do
                         board usa. Numa caixa que já tem o botão principal dentro, o
                         mesmo traço dizia a coisa errada. Virou régua sólida à
                         esquerda, que marca o bloco sem cercá-lo. */
          <Box
            bg="purple.50"
            borderLeftWidth="3px"
            borderLeftColor="purple.400"
            borderRadius="lg"
            px={3.5}
            py={3}
            mb={etapa.campos?.length ? 3.5 : 0}
          >
            <Flex align="center" gap={2} wrap="wrap" mb={2}>
              {/* O botão segue o estado do contrato vinculado (casos de uso de 30/09). */}
              {acaoOp && (estadoContrato === 'sem' || estadoContrato === 'sumiu') && (
                <>
                  <Button
                    leftIcon={<FileText size={16} />}
                    colorScheme="purple"
                    size="sm"
                    fontSize="13px"
                    borderRadius="full"
                    px={5}
                    onClick={gerarContrato}
                  >
                    {etapa.acao.label}
                  </Button>
                  <Button
                    leftIcon={<LinkSimple size={15} />}
                    variant="ghost"
                    colorScheme="purple"
                    size="sm"
                    fontSize="13px"
                    borderRadius="full"
                    onClick={() => setVincularAberto(true)}
                  >
                    Vincular contrato existente
                  </Button>
                  <Tooltip
                    hasArrow
                    placement="top"
                    openDelay={300}
                    label="Feito fora do sistema: o arquivo vai para os anexos e o item de documento gerado fica marcado."
                  >
                    <Button
                      leftIcon={<UploadSimple size={15} />}
                      variant="ghost"
                      colorScheme="purple"
                      size="sm"
                      fontSize="13px"
                      borderRadius="full"
                      isLoading={docDeForaMutation.isPending}
                      isDisabled={!card?.id}
                      onClick={() => docDeForaRef.current?.click()}
                    >
                      {tipo === 'constituicao' ? 'Contrato feito fora' : 'Documento feito fora'}
                    </Button>
                  </Tooltip>
                  <input
                    ref={docDeForaRef}
                    type="file"
                    hidden
                    aria-label="Arquivo feito fora do sistema"
                    onChange={escolheuDocDeFora}
                  />
                </>
              )}
              {estadoContrato === 'carregando' && (
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="purple"
                  borderRadius="full"
                  isLoading
                  loadingText={`Contrato #${contratoId}`}
                />
              )}
              {estadoContrato === 'edicao' && (
                <Button
                  leftIcon={<PencilSimple size={16} />}
                  colorScheme="purple"
                  size="sm"
                  fontSize="13px"
                  borderRadius="full"
                  px={5}
                  onClick={continuarContrato}
                >
                  Continuar contrato #{contratoId}
                </Button>
              )}
              {(estadoContrato === 'pronto' ||
                estadoContrato === 'fora' ||
                estadoContrato === 'erro') && (
                <Button
                  leftIcon={<ArrowSquareOut size={16} />}
                  colorScheme="purple"
                  variant="outline"
                  size="sm"
                  fontSize="13px"
                  borderRadius="full"
                  px={5}
                  onClick={abrirContrato}
                >
                  Abrir contrato #{contratoId}
                </Button>
              )}
              {/* Sem gerador (distrato): um "Gerar" travado para sempre parecia defeito. */}
              {!acaoOp && !temContrato && (
                <Flex as="span" display="inline-flex" align="center" gap={2}>
                  <FileText size={16} color="var(--chakra-colors-purple-600)" />
                  <Text as="span" fontSize="13px" fontWeight="600" color="purple.800">
                    {etapa.acao.label}
                  </Text>
                </Flex>
              )}
              {temContrato && estadoContrato !== 'carregando' && estadoContrato !== 'erro' && (
                <Text
                  as="span"
                  fontSize="12px"
                  color={estadoContrato === 'fora' ? 'orange.700' : 'gray.600'}
                >
                  {rotuloDoStatus(contratoVinculado?.status)}
                  {contratoVinculado?.dt_atualizacao &&
                    ` · ${new Date(contratoVinculado.dt_atualizacao).toLocaleString('pt-BR', {
                      day: '2-digit',
                      month: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}`}
                </Text>
              )}
              {temContrato && (
                <Menu placement="bottom-end" isLazy>
                  <MenuButton
                    as={IconButton}
                    aria-label="Mais ações do contrato"
                    icon={<DotsThreeVertical size={16} weight="bold" />}
                    size="xs"
                    variant="ghost"
                    borderRadius="full"
                  />
                  <MenuList fontSize="13px" minW="200px">
                    <MenuItem icon={<Swap size={14} />} onClick={() => setVincularAberto(true)}>
                      Trocar contrato…
                    </MenuItem>
                    <MenuItem icon={<LinkBreak size={14} />} onClick={desvincularContrato}>
                      Desvincular
                    </MenuItem>
                  </MenuList>
                </Menu>
              )}
              {/* A nota da ação saiu daqui de baixo e virou este "?". Eram 2 a 4
                              frases fixas, em jargão de quem construiu ("gerador v3",
                              "AUDITOR de fidelidade", "vigente × render"), lidas uma vez e
                              depois só ocupando a altura da caixa a cada abertura do card. */}
              <Ajuda
                rotulo="Como esta ação funciona"
                texto={
                  <>
                    {etapa.acao.nota}
                    {registry?.extras?.facilitaSp && acaoOp === 'constituicao' && (
                      <>
                        {' '}
                        Modelo próprio (fiel ao cliente) ⇒ registro tradicional — sem deferimento
                        automático do Facilita SP, que exige o contrato-padrão do sistema.
                      </>
                    )}
                  </>
                }
              />
            </Flex>
            {estadoContrato === 'sumiu' && (
              <Text fontSize="12px" color="orange.700" mb={2}>
                O contrato #{contratoId} vinculado não existe mais (foi descartado ou apagado). Gere
                outro ou vincule um existente.
              </Text>
            )}
            {estadoContrato === 'fora' && (
              <Text fontSize="12px" color="orange.700" mb={2}>
                O contrato #{contratoId} está {rotuloDoStatus(contratoVinculado?.status)}. Troque
                por outro ou desvincule pelo menu ⋮.
              </Text>
            )}
            {estadoContrato === 'erro' && (
              <Text fontSize="12px" color="orange.700" mb={2}>
                Não foi possível ler o contrato #{contratoId}.{' '}
                <Button
                  variant="link"
                  size="xs"
                  fontSize="12px"
                  colorScheme="orange"
                  verticalAlign="baseline"
                  onClick={() => releContrato()}
                >
                  Tentar de novo
                </Button>
              </Text>
            )}
            {/* Rodadas de "cliente não aprovou → refaz". Só aparece quando houve
                          alguma — em processo de 1ª geração seria ruído. */}
            {regeracoes > 0 && (
              <Tooltip
                hasArrow
                label={`O card já viu ${versoesVistas.length} versões distintas deste documento. Contagem do que o card observou — regerar duas vezes seguidas sem reabrir o card conta como uma.`}
              >
                <Badge
                  colorScheme="orange"
                  fontSize="10px"
                  borderRadius="full"
                  px={2}
                  mb={2}
                  cursor="default"
                >
                  {regeracoes === 1 ? 'regerado 1×' : `regerado ${regeracoes}×`}
                </Badge>
              </Tooltip>
            )}
            {!temContrato && typeof estadoEtapa[CHAVE_DOC_DE_FORA] === 'string' && (
              <Text fontSize="12px" color="gray.600" mb={2}>
                Feito fora do sistema: <b>{estadoEtapa[CHAVE_DOC_DE_FORA] as string}</b> (na aba
                Anexos).
              </Text>
            )}
          </Box>
        )}
        {/* Procedência primeiro (de onde o dado vem), depois o que se digita. */}
        {camposProcedencia.length ? renderProcedencia(camposProcedencia) : null}

        {/* Campos em ate 3 colunas: 18 das 25 etapas tem no maximo 1 campo,
                        mas a mais cheia tem 9 — grade fixa de 2 desperdicava a largura do
                        modal justamente onde ela era necessaria.
                        `alignItems="end"` alinha os INPUTS pela base: se um rótulo da
                        linha quebrar em duas linhas, os campos vizinhos continuam na
                        mesma altura em vez de escalonar. */}
        {camposEditaveis.length ? (
          <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={3} alignItems="end">
            {camposEditaveis.map(renderCampo)}
          </SimpleGrid>
        ) : null}

        {/* CHECKLIST embutido no card da etapa — antes era um GlassCard proprio.
                        Em 10 das 25 etapas nao ha campo nenhum, e este card renderizava so o
                        texto "Sem campos proprios": card vazio em 40% dos casos, com o
                        checklist logo abaixo num segundo card. Fundidos, o card sempre tem
                        conteudo — o checklist e o UNICO bloco presente nas 25 etapas. */}
        {checklistEfetivo.length ? (
          <Box mt={etapa?.campos?.length || etapa?.acao ? 4 : 0}>
            <SectionTitle
              icon={ListChecks}
              accent="#1B88F5"
              // Só quando "Nesta etapa" não renderizou — senão a mesma
              // explicação apareceria duas vezes na mesma coluna.
              ajuda={etapa?.acao || etapa?.campos?.length ? undefined : etapa?.nota}
            >
              Checklist da etapa
            </SectionTitle>
            {subpassosRevisar ? (
              <Text fontSize="11px" color="orange.600" fontWeight="500" mb={3}>
                ⚠️ Tipos de alteração ainda não definidos — exibindo todos os pré-requisitos por
                segurança. Revise em “Definição + Alteração”.
              </Text>
            ) : null}
            {subpassos ? (
              <Stack spacing={4}>
                {/* A pergunta da exigência vale para a etapa inteira: vem antes dos sub-passos. */}
                {checklistEfetivo.filter(c => c.key === CHAVE_EXIGENCIA).map(renderCheckRow)}
                {subpassos.map(sp => (
                  <Box key={sp.key}>
                    <Flex align="baseline" gap={2} mb={2}>
                      <Text fontSize="13px" fontWeight="700" color="gray.700">
                        {sp.label}
                      </Text>
                      <Text fontSize="11px" color="gray.500">
                        {sp.orgao}
                      </Text>
                    </Flex>
                    <Stack spacing={2}>{sp.checklist.map(renderCheckRow)}</Stack>
                  </Box>
                ))}
              </Stack>
            ) : (
              <Stack spacing={2}>{checklistEfetivo.map(renderCheckRow)}</Stack>
            )}
          </Box>
        ) : null}
      </Box>

      {/* Rota B numa etapa só da Rota A (ou "não será cliente" em Demais órgãos e no Licenciamento): não se aplica — sem
          checklist, avança direto. */}
      {etapaPuladaRotaB && (
        <Box borderWidth="1px" borderColor="gray.100" borderRadius="xl" p={4}>
          <Flex align="center" gap={2.5}>
            <Info size={18} weight="duotone" color="#64748B" />
            <Text fontSize="13px" color="gray.600">
              {tipo === 'alteracao' ? (
                <>
                  <b>Não se aplica (Rota B).</b> Alteração de capital, quadro de sócios ou
                  administração não passa por esta etapa
                </>
              ) : (
                <>
                  <b>Não se aplica.</b> A empresa não será cliente da Especialistas
                </>
              )}{' '}
              — avance direto
              {proxima ? ` para ${proxima}` : ''}.
            </Text>
          </Flex>
        </Box>
      )}

      {/* Já coletado (etapas anteriores); a pulada não entra, como no stepper */}
      {coletadas.length > 0 && (
        <Box borderWidth="1px" borderColor="gray.100" borderRadius="xl" p={4}>
          <SectionTitle icon={CheckCircle} accent="#10B981">
            Já coletado ({coletadas.length} etapa(s))
          </SectionTitle>
          <Stack spacing={1.5}>
            {coletadas.map(col => {
              const assumida = etapaAssumida(legalizacao.estado_etapas, col);
              return (
                <Flex
                  key={col}
                  align="center"
                  gap={2}
                  px={2}
                  py={1}
                  mx={-2}
                  borderRadius="md"
                  cursor="pointer"
                  transition="background .12s"
                  _hover={{ bg: 'gray.50' }}
                  onClick={() => setViewingCol(col)}
                  title="Ver dados desta etapa"
                >
                  {assumida ? (
                    <Box
                      w="15px"
                      h="15px"
                      borderRadius="full"
                      borderWidth="1.5px"
                      borderStyle="dashed"
                      borderColor="gray.400"
                      flexShrink={0}
                    />
                  ) : (
                    <CheckCircle size={15} weight="fill" color="var(--chakra-colors-green-500)" />
                  )}
                  <Text fontSize="13px" color={assumida ? 'gray.500' : 'gray.600'}>
                    {col}
                    {assumida && (
                      <Text as="span" fontSize="11px" color="gray.500" ml={1}>
                        (assumida)
                      </Text>
                    )}
                  </Text>
                </Flex>
              );
            })}
          </Stack>
        </Box>
      )}
    </Stack>
  );

  // Trilha das etapas: concluída abre em leitura; assumida e pulada pela Rota B em cinza.
  const trilha = (
    <Flex overflowX="auto" py={1} gap={1}>
      {colunas.map((col, i) => {
        let estado: 'done' | 'now' | 'todo' = 'todo';
        if (i < idx) estado = 'done';
        else if (i === idx) estado = 'now';
        const st = STEP_STYLE[estado];
        // Etapa "assumida" (feita fora do sistema via "Iniciar aqui"): concluída
        // porém SEM dado por trás → sinal visual distinto (cinza tracejado).
        const assumida = estado === 'done' && etapaAssumida(legalizacao.estado_etapas, col);
        // Rota B pula as etapas `somenteRotaA` → cinza + não clicável no stepper.
        const pulada = etapaPuladaNaRota(tipo, col, rotaEfetiva, naoCliente);
        const cinza = assumida || pulada;
        let tooltipLabel = '';
        if (pulada)
          tooltipLabel =
            tipo === 'alteracao' ? 'Não se aplica (Rota B)' : 'Não se aplica (não será cliente)';
        else if (assumida) tooltipLabel = 'Assumida — feita fora do sistema';
        return (
          <Flex
            key={col}
            direction="column"
            align="center"
            minW="58px"
            flexShrink={0}
            cursor={estado === 'done' && !pulada ? 'pointer' : 'default'}
            onClick={estado === 'done' && !pulada ? () => setViewingCol(col) : undefined}
          >
            <Tooltip label={tooltipLabel} isDisabled={!cinza} hasArrow fontSize="11px">
              <Flex
                w="22px"
                h="22px"
                borderRadius="full"
                align="center"
                justify="center"
                fontSize="10px"
                fontWeight="700"
                bg={cinza ? 'gray.100' : st.bg}
                color={cinza ? 'gray.400' : st.color}
                border="2px"
                borderStyle={cinza ? 'dashed' : 'solid'}
                borderColor={cinza ? 'gray.300' : st.border}
                boxShadow={estado === 'now' ? '0 0 0 4px #EAF3FE' : undefined}
              >
                {estado === 'done' ? <Check size={11} weight="bold" /> : i + 1}
              </Flex>
            </Tooltip>
            <Text
              fontSize="10px"
              color={cinza ? 'gray.400' : st.label}
              fontWeight={st.weight}
              textAlign="center"
              mt={1}
              noOfLines={2}
            >
              {col.split(/[·+]/)[0].trim()}
            </Text>
          </Flex>
        );
      })}
    </Flex>
  );

  // Portal integrador da UF (2026-08-06, pedido da Juliana): atalho fixo pro sistema onde a
  // maior parte das etapas acontece, pro operador não ter que caçar o endereço a cada processo.
  // Só aparece quando o registry resolve (UF/município conhecidos) e tem URL cadastrada.
  const atalhos = (registry?.portalUrl || linkDaColuna || registry?.generico) && (
    <Flex gap={2} wrap="wrap">
      {/* AVISO DE FLUXO GENÉRICO.
                      A UF não foi levantada: o fluxo abaixo é o padrão nacional
                      (REDESIM/DREI), verificado em 8 estados mas NÃO nesta. Sem este
                      aviso o genérico é indistinguível de um registry validado — e a
                      pesquisa de 08/17 mostrou que em 2 dos 3 estados levantados o
                      padrão nacional NÃO valia, e a diferença era um passo obrigatório
                      (localização eliminatória no DF, TVL da SUCOM em Salvador).
                      Fica ANTES do link do portal: é ressalva sobre tudo que vem
                      depois, não um atalho a mais. */}
      {registry?.generico && (
        <Flex
          align="center"
          gap={2}
          px={3}
          py={2}
          borderWidth="1px"
          borderColor="orange.200"
          bg="orange.50"
          borderRadius="lg"
        >
          <Warning size={15} weight="fill" color="var(--chakra-colors-orange-500)" />
          <Text fontSize="13px" color="orange.800" fontWeight="500">
            Fluxo genérico ({registry.nome})
          </Text>
          {/* O AVISO continua na tela — não virou "?" como as explicações.
                          Ele não explica nada: diz que o roteiro abaixo pode estar
                          incompleto NESTE estado. A pesquisa de 17/08 achou passo
                          obrigatório fora do padrão nacional em 2 dos 3 estados
                          levantados. Escondê-lo tornaria o genérico indistinguível de um
                          registry validado. Só o complemento foi para o tooltip. */}
          <Ajuda
            rotulo="O que significa fluxo genérico"
            texto="Este estado ainda não foi levantado. As etapas abaixo seguem o padrão nacional (REDESIM/DREI), que vale na maioria dos casos mas não em todos — confira as exigências direto na junta antes de protocolar."
          />
        </Flex>
      )}
      {registry?.portalUrl && (
        <Link
          href={registry.portalUrl}
          isExternal
          display="flex"
          alignItems="center"
          gap={2}
          px={3}
          py={2}
          borderWidth="1px"
          borderColor="blue.100"
          bg="blue.50"
          borderRadius="lg"
          _hover={{ bg: 'blue.100', textDecoration: 'none' }}
        >
          <ArrowSquareOut size={15} weight="bold" color="var(--chakra-colors-blue-600)" />
          <Text fontSize="13px" color="blue.800" fontWeight="500">
            {registry.portal}
          </Text>
          <Text fontSize="11px" color="blue.600">
            · {registry.nome}
          </Text>
        </Link>
      )}
      {/* Atalho do sistema EXTERNO desta etapa — hoje só o Coletor Nacional
                    (RFB), que é o único ato fora do portal da junta. Cor distinta
                    (roxo) pro operador não confundir com o portal da UF. */}
      {linkDaColuna && (
        <Link
          href={linkDaColuna.url}
          isExternal
          display="flex"
          alignItems="center"
          gap={2}
          px={3}
          py={2}
          borderWidth="1px"
          borderColor="purple.100"
          bg="purple.50"
          borderRadius="lg"
          _hover={{ bg: 'purple.100', textDecoration: 'none' }}
        >
          <ArrowSquareOut size={15} weight="bold" color="var(--chakra-colors-purple-600)" />
          <Text fontSize="13px" color="purple.800" fontWeight="500">
            {linkDaColuna.label}
          </Text>
        </Link>
      )}
    </Flex>
  );

  // Linha abaixo do nome: QUEM é o processo (tipo, rota, identificadores, contrato), como no
  // cabeçalho do drawer antigo. A lateral fica com a situação (etapa, prazo, tempo, dono).
  const subtitulo = (
    <Flex align="center" columnGap={3} rowGap={1.5} wrap="wrap">
      <Flex align="center" gap={1.5}>
        <Badge bg={COR_TIPO[tipo].bg} color={COR_TIPO[tipo].text} borderRadius="md">
          {TIPO_LEGALIZACAO_LABEL[tipo]}
        </Badge>
        {legalizacao.rota && (
          <Badge colorScheme={legalizacao.rota === 'A' ? 'orange' : 'green'} borderRadius="md">
            Rota {legalizacao.rota}
          </Badge>
        )}
      </Flex>
      {/* Só os que já existem (em constituição CNPJ e NIRE só surgem no deferimento). Clicar
          copia: existem para serem colados em portal. */}
      {identificadores.map(item => (
        <Flex
          key={item.rotulo}
          as={item.copiavel ? 'button' : undefined}
          type={item.copiavel ? 'button' : undefined}
          onClick={item.copiavel ? () => copiar(item.valor, item.rotulo) : undefined}
          align="baseline"
          gap={1.5}
          borderRadius="md"
          px={1}
          mx={-1}
          transition="background .12s"
          _hover={item.copiavel ? { bg: 'gray.100' } : undefined}
          _focusVisible={{ outline: '2px solid #7C3AED', outlineOffset: '1px' }}
          title={item.copiavel ? 'Clique para copiar' : undefined}
        >
          <Text
            fontSize="10px"
            fontWeight="700"
            color="gray.500"
            textTransform="uppercase"
            letterSpacing="0.06em"
          >
            {item.rotulo}
          </Text>
          <Text
            fontSize="13px"
            fontWeight="500"
            color="gray.700"
            sx={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {item.valor}
          </Text>
        </Flex>
      ))}
      <Tooltip hasArrow label="Trocar estado ou município">
        <IconButton
          aria-label="Trocar estado ou município"
          icon={<MapPin size={14} weight="bold" />}
          size="xs"
          variant="ghost"
          borderRadius="md"
          color="gray.400"
          _hover={{ color: 'purple.600', bg: 'purple.50' }}
          onClick={() => setTrocarLocalAberto(true)}
        />
      </Tooltip>
      {/* ATALHO DO CONTRATO em QUALQUER etapa (2026-08-27, operador): assim que o card
          avançava, o contrato que ele gerou ficava inalcançável por ele. Fora do gerado,
          diz o estado ("rascunho", "arquivado"). */}
      {contratoId && temContrato && (
        <Button
          as="a"
          href={urlDaListagem(contratoId)}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            foiAoContratoRef.current = true;
          }}
          size="xs"
          height="22px"
          px={2}
          fontSize="11px"
          variant="outline"
          colorScheme="purple"
          borderRadius="full"
          rightIcon={<ArrowSquareOut size={11} />}
        >
          Contrato #{contratoId}
          {(estadoContrato === 'edicao' || estadoContrato === 'fora') &&
            ` · ${rotuloDoStatus(contratoVinculado?.status)}`}
        </Button>
      )}
    </Flex>
  );

  // Fixo acima das abas, como no drawer antigo (user, 30/09): quem é o processo, a trilha e os
  // atalhos valem em qualquer aba e não rolam com o conteúdo da etapa.
  const cabecalho = (
    <Stack spacing={3}>
      {subtitulo}
      {trilha}
      {atalhos}
    </Stack>
  );

  // Barra lateral: a SITUAÇÃO do processo e as ações, como o Estatuto usa o slot do modal
  // genérico. O prazo continua mudando só com motivo (`SlaPrazo`), por isso a expiração
  // genérica fica escondida.
  const lateral = (
    <Stack spacing={4}>
      <Box>
        <RotuloLateral>Etapa</RotuloLateral>
        <Flex align="center" gap={2}>
          <Text fontSize="13px" fontWeight="600" color="gray.800" lineHeight="1.3">
            {columnTitle}
          </Text>
          <FluxoIcons markers={fluxoDaEtapa(tipo, columnTitle)} size={13} />
        </Flex>
        {/* O destino do "Avançar etapa" mora aqui: o nome inteiro da coluna não cabe no botão. */}
        {proxima && (
          <Text fontSize="12px" color="gray.600" mt={0.5}>
            Próxima: {proxima}
          </Text>
        )}
      </Box>

      {card && (
        <Box>
          <RotuloLateral>Prazo (SLA)</RotuloLateral>
          <SlaPrazo
            key={card.id}
            card={card}
            legalizacao={legalizacao}
            onChanged={invalidar}
            recuo="0"
          />
        </Box>
      )}

      {card && (
        <Box>
          <RotuloLateral>Tempo</RotuloLateral>
          <Text fontSize="13px" color="gray.800" sx={{ fontVariantNumeric: 'tabular-nums' }}>
            <Text as="span" fontWeight="700">
              {formatDuracao(card.dias_etapa_atual) ?? '—'}
            </Text>{' '}
            nesta etapa
          </Text>
          <Text fontSize="12px" color="gray.600" sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {formatDuracao(card.dias_total) ?? '—'} no total
          </Text>
        </Box>
      )}

      <Box>
        <RotuloLateral>Responsável</RotuloLateral>
        <ResponsavelPicker nomeAtual={respNome} onSelect={reatribuir} />
      </Box>

      <Divider />

      {/* AVANÇO. Nomeia o primeiro pendente (não só quantos faltam) e diz quando a etapa
          nem está no playbook, em vez de travar em silêncio. */}
      <Stack spacing={2}>
        {!podeAvancar && (
          // Limite pelo texto da exigência, que pode ser um despacho inteiro (ele está na etapa).
          <Text fontSize="11px" color="orange.600" lineHeight="1.4" noOfLines={4}>
            {acaoPendente.texto}
            {acaoPendente.restantes > 0 && ` · +${acaoPendente.restantes} obrigatório(s)`}
          </Text>
        )}
        <Button
          colorScheme="blue"
          w="full"
          size="sm"
          borderRadius="lg"
          rightIcon={iconeDoAvanco}
          onClick={() => (proxima ? avancarMutation.mutate() : setFinalizarAberto(true))}
          isDisabled={!podeAvancar}
          isLoading={avancarMutation.isPending || finalizarMutation.isPending}
        >
          {proxima ? 'Avançar etapa' : 'Finalizar processo'}
        </Button>
        <Button
          w="full"
          size="sm"
          borderRadius="lg"
          variant={salvoOk ? 'solid' : 'outline'}
          colorScheme={salvoOk ? 'green' : 'gray'}
          bg={salvoOk ? undefined : 'white'}
          leftIcon={salvoOk ? <Check size={14} weight="bold" /> : undefined}
          onClick={() => salvarMutation.mutate(undefined)}
          isLoading={salvarMutation.isPending}
        >
          {salvoOk ? 'Salvo' : 'Salvar etapa'}
        </Button>
      </Stack>

      {/* Mesmo desenho do "Arquivar card" do genérico (que fica escondido: este pede motivo). */}
      {card?.id && (
        <>
          <Divider />
          <Button
            size="sm"
            variant="ghost"
            leftIcon={<Archive size={14} />}
            color="red.400"
            w="100%"
            justifyContent="flex-start"
            fontWeight="500"
            fontSize="12px"
            borderRadius="lg"
            _hover={{ bg: 'red.50', color: 'red.500' }}
            onClick={() => setArquivarAberto(true)}
            isLoading={arquivarMutation.isPending}
          >
            Arquivar processo
          </Button>
        </>
      )}
    </Stack>
  );

  return (
    <>
      {/* 2026-09-29: o modal próprio (cabeçalho, duas colunas e rodapé) deu lugar ao modal
          GENÉRICO do Kanban, como o Estatuto: aba "Etapa" com o trabalho da etapa, as abas
          de sempre (Comentários, Anexos, Atividade) e a lateral com contexto e ações. A
          lógica da etapa (gate, derivados do contrato, dispensas) continua toda aqui. */}
      <KanbanCardModal
        isOpen={isOpen}
        onClose={fechar}
        card={card}
        columnTitle={columnTitle}
        // Edição genérica desligada (`hideEdit`): o nome muda pelo `renomearAcao`, e o
        // resto (prazo, responsável) pelos controles da própria legalização na lateral.
        onSave={() => undefined}
        onArchive={() => setArquivarAberto(true)}
        hideEdit
        tituloAcao={renomearAcao}
        cabecalho={cabecalho}
        hideArchive
        hideDescription
        hidePriority
        hideExpiracao
        hideResponsaveis
        hideAssinatura
        size="5xl"
        altura="90vh"
        extraTab={{
          key: 'etapa',
          label: 'Etapa',
          icon: <ListChecks size={13} weight="bold" />,
          content: abaEtapa,
        }}
        sidebarSlot={lateral}
        anexosContent={
          card?.id ? (
            <CardAnexos cardId={card.id} empresaNome={card.titulo} etapaNome={columnTitle} />
          ) : null
        }
        comentariosAnteriores={obsAnteriorAosComentarios}
        comentariosDoMaisNovo
        onComentarioPendente={guardarEnvioDoComentario}
      />

      <Modal isOpen={!!viewingCol} onClose={() => setViewingCol(null)} isCentered>
        <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(4px)" />
        <ModalContent bg="white" borderRadius="xl">
          <ModalHeader fontSize="18px" fontWeight="700">
            {viewingCol}
            {viewingCol && etapaAssumida(legalizacao.estado_etapas, viewingCol) ? (
              <Badge colorScheme="gray" ml={2}>
                assumida
              </Badge>
            ) : (
              <Badge colorScheme="green" ml={2}>
                concluída
              </Badge>
            )}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={5}>{viewingCol && renderReadOnly(viewingCol)}</ModalBody>
        </ModalContent>
      </Modal>

      {/* `nomeJaAprovado`: quem resolve é o drawer, porque a CHAVE do item de nome muda
          entre registry e fallback e entre os tipos (`nome_aprovado` / `nome_end_aprovado`).
          Casar por regex na chave cobre as variações sem o modal precisar conhecer o
          playbook. */}
      <RenomearProcessoModal
        isOpen={renomearAberto}
        onClose={() => setRenomearAberto(false)}
        nomeAtual={card?.titulo}
        nomeJaAprovado={nomeAprovadoNaViabilidade}
        isLoading={renomearMutation.isPending}
        onConfirm={titulo => {
          if (card?.id) renomearMutation.mutate({ cardId: card.id, titulo });
        }}
      />

      <TrocarLocalModal
        isOpen={trocarLocalAberto}
        onClose={() => setTrocarLocalAberto(false)}
        ufAtual={legalizacao.junta_uf}
        municipioAtual={legalizacao.municipio}
        isLoading={trocarLocalMutation.isPending}
        onConfirm={local => trocarLocalMutation.mutate(local)}
      />

      <FinalizarProcessoModal
        isOpen={finalizarAberto}
        onClose={() => setFinalizarAberto(false)}
        titulo={card?.titulo}
        isLoading={finalizarMutation.isPending}
        onConfirm={() => finalizarMutation.mutate()}
      />

      <VincularContratoModal
        isOpen={vincularAberto}
        onClose={() => setVincularAberto(false)}
        legalizacao={legalizacao}
        tipo={tipo}
        titulo={card?.titulo}
        contratoAtualId={temContrato ? contratoId : null}
        isLoading={vincularMutation.isPending}
        onConfirm={id => vincularMutation.mutate(id)}
      />

      <ArquivarProcessoModal
        isOpen={arquivarAberto}
        onClose={() => setArquivarAberto(false)}
        titulo={card?.titulo}
        isLoading={arquivarMutation.isPending}
        onConfirm={motivo => {
          if (card?.id) arquivarMutation.mutate({ cardId: card.id, motivo });
        }}
      />

      {/* A dispensa entra no estado LOCAL da etapa e só vai pro servidor no "Salvar",
          como qualquer outra marcação do checklist. Um PATCH imediato aqui gravaria a
          dispensa mesmo que o operador fechasse o drawer sem salvar o resto — dois
          caminhos de escrita pro mesmo bloco, discordando entre si. */}
      <DispensarItemModal
        isOpen={!!dispensando}
        onClose={() => setDispensando(null)}
        item={dispensando?.label}
        onConfirm={(motivo, observacao) => {
          if (dispensando) dispensarItem(dispensando.key, motivo, observacao);
          setDispensando(null);
        }}
      />
    </>
  );
};

export default LegalizacaoDrawer;
